import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Divider,
  Progress,
  Group,
  Modal,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconArrowRight,
  IconArrowsExchange,
  IconBell,
  IconCheck,
  IconCircleCheck,
  IconClipboard,
  IconLock,
  IconLockOpen,
  IconCopy,
  IconDownload,
  IconExternalLink,
  IconInfoCircle,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSparkles,
  IconTrash,
  IconUserCheck,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { AblaufplanTab } from '../components/forms/AblaufplanTab';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../lib/auth/useAuth';
import {
  availabilityColor,
  availabilityLabel,
  buildDefaultFormPushBody,
  buildSubmissionPreview,
  clipFormPushText,
  DEFAULT_FORM_PUSH_TITLE,
  fieldKeyFromLabel,
  FORM_FIELD_TYPE_OPTIONS,
  formatStoredAnswerValue,
  getFormAvailabilityState,
  isSelectionField,
  isPersonListField,
  isStaticField,
  linesToOptions,
  normalizeFormPersonListEntries,
  normalizeProfileSuggestion,
  normalizePersonListEntries,
  normalizePersonMatchToken,
  optionsToLines,
  personListUsesProfileSuggestions,
  resolveFormPushBody,
  submissionSourceColor,
  submissionSourceLabel,
  statusColor,
  statusLabel,
} from '../lib/forms';
import {
  bedroomRoomPrefix,
  buildRoomPlanningParticipantIdentityIndex,
  buildMissingBedroomRoomInserts,
  normalizeBedroomRooms,
  buildRoomPlanningConflicts,
  buildRoomPlanningParticipants,
  buildRoomProposal,
  buildRoomWishGroupSummaries,
  countParticipantsWithoutWishes,
  resolveRoomPlanningAssignmentParticipant,
  roomPlanningParticipantKey,
  roomPlanningConflictColor,
  roomPlanningConflictLabel,
} from '../lib/roomPlanning';
import type {
  RoomPlanningParticipant,
} from '../lib/roomPlanning';
import type {
  FormFieldRow,
  FormRoomAssignmentRow,
  FormRoomContingentRow,
  FormRoomPlanningConfigRow,
  FormRoomRow,
  FormRow,
  FormStatus,
  FormSubmissionAnswerRow,
  FormSubmissionRow,
  FormSubmissionWithAnswers,
  FormProfileSuggestion,
} from '../lib/forms';
import { fetchPublicProfiles } from '../lib/publicProfiles';
import { slugify } from '../lib/slug';
import { supabase } from '../lib/supabaseClient';

type FormEditorState = {
  id: string;
  title: string;
  slug: string;
  shortDescription: string;
  introText: string;
  status: FormStatus;
  startsAt: string;
  endsAt: string;
  showOnHomepage: boolean;
  homepageTitle: string;
  homepageText: string;
  homepageCtaLabel: string;
  pushBody: string;
  pushBodyTouched: boolean;
  submitButtonLabel: string;
  confirmationTitle: string;
  confirmationMessage: string;
  allowSubmissionEdit: boolean;
};

type FieldEditorState = {
  id: string;
  fieldKey: string;
  fieldKeyTouched: boolean;
  label: string;
  fieldType: FormFieldRow['field_type'];
  helpText: string;
  placeholder: string;
  staticText: string;
  optionsText: string;
  profileSuggestions: boolean;
  isRequired: boolean;
  sortOrder: string;
};

type SelectionFieldStats = {
  field: FormFieldRow;
  answeredCount: number;
  entries: Array<{ label: string; count: number; share: number }>;
};

type NumericFieldStats = {
  field: FormFieldRow;
  answeredCount: number;
  average: number;
  min: number;
  max: number;
};

type PersonListMatchGroup = {
  sharedNames: string[];
  submissions: Array<{
    submission: FormSubmissionWithAnswers;
    names: string[];
  }>;
};

type PersonListFieldInsight = {
  field: FormFieldRow;
  groups: PersonListMatchGroup[];
};

type RoomContingentEditorState = {
  id: string;
  roomCount: string;
  capacity: string;
  labelPrefix: string;
  sortOrder: string;
};

type RoomEditorState = {
  name: string;
  capacity: string;
  notes: string;
};

type ManualRoomParticipantState = {
  name: string;
  participantId: string | null;
  roomId: string;
};

type AnalysisTab = 'submissions' | 'stats' | 'rooms' | 'ablaufplan';

type RoomWishNameMatchPayload = {
  rawName: string;
  userId: string;
};

function emptyFormEditor(): FormEditorState {
  return {
    id: '',
    title: '',
    slug: '',
    shortDescription: '',
    introText: '',
    status: 'draft',
    startsAt: '',
    endsAt: '',
    showOnHomepage: false,
    homepageTitle: '',
    homepageText: '',
    homepageCtaLabel: '',
    pushBody: '',
    pushBodyTouched: false,
    submitButtonLabel: 'Absenden',
    confirmationTitle: 'Vielen Dank',
    confirmationMessage: 'Deine Anmeldung wurde gespeichert.',
    allowSubmissionEdit: false,
  };
}

function emptyFieldEditor(sortOrder = '0'): FieldEditorState {
  return {
    id: '',
    fieldKey: '',
    fieldKeyTouched: false,
    label: '',
    fieldType: 'text_input',
    helpText: '',
    placeholder: '',
    staticText: '',
    optionsText: '',
    profileSuggestions: true,
    isRequired: false,
    sortOrder,
  };
}

function emptyRoomContingentEditor(sortOrder = '0'): RoomContingentEditorState {
  return {
    id: '',
    roomCount: '1',
    capacity: '4',
    labelPrefix: '',
    sortOrder,
  };
}

function emptyRoomEditor(): RoomEditorState {
  return {
    name: '',
    capacity: '4',
    notes: '',
  };
}

function splitPersonListText(value: string | null): string[] {
  if (!value?.trim()) return [];
  return Array.from(
    new Set(
      value
        .split(/\s*(?:\||,|;|\n|\r|\/|\+|&|\bund\b)\s*/iu)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0),
    ),
  );
}

function nextFieldSortOrder(fields: FormFieldRow[]): string {
  const highestSortOrder = fields.reduce((max, field) => Math.max(max, field.sort_order), -1);
  return String(highestSortOrder + 1);
}

function nextRoomContingentSortOrder(contingents: FormRoomContingentRow[]): string {
  const highestSortOrder = contingents.reduce((max, entry) => Math.max(max, entry.sort_order), -1);
  return String(highestSortOrder + 1);
}

function mapFormRow(row: Record<string, unknown>): FormRow {
  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? ''),
    slug: String(row.slug ?? ''),
    short_description: String(row.short_description ?? ''),
    intro_text: String(row.intro_text ?? ''),
    submit_button_label: String(row.submit_button_label ?? 'Absenden'),
    confirmation_title: String(row.confirmation_title ?? 'Vielen Dank'),
    confirmation_message: String(row.confirmation_message ?? 'Deine Anmeldung wurde gespeichert.'),
    allow_submission_edit: row.allow_submission_edit === true,
    status: String(row.status ?? 'draft') as FormStatus,
    starts_at: row.starts_at ? String(row.starts_at) : null,
    ends_at: row.ends_at ? String(row.ends_at) : null,
    show_on_homepage: row.show_on_homepage === true,
    homepage_title: row.homepage_title ? String(row.homepage_title) : null,
    homepage_text: row.homepage_text ? String(row.homepage_text) : null,
    homepage_cta_label: row.homepage_cta_label ? String(row.homepage_cta_label) : null,
    push_body: row.push_body ? String(row.push_body) : null,
    published_at: row.published_at ? String(row.published_at) : null,
    notification_sent_at: row.notification_sent_at ? String(row.notification_sent_at) : null,
    notification_sent_by: row.notification_sent_by ? String(row.notification_sent_by) : null,
    last_status: row.last_status ? String(row.last_status) as FormStatus : null,
    public_url: String(row.public_url ?? ''),
    created_by: String(row.created_by ?? ''),
    updated_by: row.updated_by ? String(row.updated_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapFieldRow(row: Record<string, unknown>): FormFieldRow {
  return {
    id: String(row.id ?? ''),
    form_id: String(row.form_id ?? ''),
    field_key: String(row.field_key ?? ''),
    field_type: String(row.field_type ?? 'text_input') as FormFieldRow['field_type'],
    label: String(row.label ?? ''),
    help_text: String(row.help_text ?? ''),
    placeholder: String(row.placeholder ?? ''),
    static_text: String(row.static_text ?? ''),
    options: Array.isArray(row.options) ? row.options.map((entry) => String(entry)) : [],
    field_settings: row.field_settings && typeof row.field_settings === 'object' && !Array.isArray(row.field_settings)
      ? row.field_settings as Record<string, unknown>
      : {},
    is_required: row.is_required === true,
    sort_order: Number(row.sort_order ?? 0),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapSubmissionRow(row: Record<string, unknown>): FormSubmissionRow {
  return {
    id: String(row.id ?? ''),
    form_id: String(row.form_id ?? ''),
    user_id: row.user_id ? String(row.user_id) : null,
    source: String(row.source ?? 'web').trim().toLowerCase() === 'app' ? 'app' : 'web',
    source_ip_hash: row.source_ip_hash ? String(row.source_ip_hash) : null,
    user_agent: row.user_agent ? String(row.user_agent) : null,
    test_submission: row.test_submission === true,
    submitted_at: String(row.submitted_at ?? ''),
  };
}

function mapSubmissionAnswerRow(row: Record<string, unknown>): FormSubmissionAnswerRow {
  return {
    submission_id: String(row.submission_id ?? ''),
    form_id: String(row.form_id ?? ''),
    field_id: row.field_id ? String(row.field_id) : null,
    field_key: String(row.field_key ?? ''),
    field_label: String(row.field_label ?? ''),
    field_type: String(row.field_type ?? 'text_input') as FormSubmissionAnswerRow['field_type'],
    field_order: Number(row.field_order ?? 0),
    value_text: row.value_text == null ? null : String(row.value_text),
    value_json: row.value_json ?? null,
    created_at: String(row.created_at ?? ''),
  };
}

function mapRoomPlanningConfigRow(row: Record<string, unknown>): FormRoomPlanningConfigRow {
  return {
    form_id: String(row.form_id ?? ''),
    enabled: row.enabled === true,
    person_list_field_id: row.person_list_field_id ? String(row.person_list_field_id) : null,
    suggestions_enabled: row.suggestions_enabled !== false,
    updated_by: row.updated_by ? String(row.updated_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapRoomContingentRow(row: Record<string, unknown>): FormRoomContingentRow {
  return {
    id: String(row.id ?? ''),
    form_id: String(row.form_id ?? ''),
    room_count: Number(row.room_count ?? 1),
    capacity: Number(row.capacity ?? 4),
    label_prefix: String(row.label_prefix ?? ''),
    sort_order: Number(row.sort_order ?? 0),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapRoomRow(row: Record<string, unknown>): FormRoomRow {
  return {
    id: String(row.id ?? ''),
    form_id: String(row.form_id ?? ''),
    contingent_id: row.contingent_id ? String(row.contingent_id) : null,
    name: String(row.name ?? ''),
    capacity: Number(row.capacity ?? 4),
    floor: String(row.floor ?? ''),
    type: String(row.type ?? 'Schlafraum'),
    is_sleeping_room: row.is_sleeping_room !== false,
    notes: String(row.notes ?? ''),
    sort_order: Number(row.sort_order ?? 0),
    is_locked: row.is_locked === true,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapRoomAssignmentRow(row: Record<string, unknown>): FormRoomAssignmentRow {
  return {
    id: String(row.id ?? ''),
    form_id: String(row.form_id ?? ''),
    room_id: row.room_id ? String(row.room_id) : null,
    submission_id: row.submission_id ? String(row.submission_id) : null,
    user_id: row.user_id ? String(row.user_id) : null,
    display_name: String(row.display_name ?? ''),
    source: String(row.source ?? 'submission') as FormRoomAssignmentRow['source'],
    sort_order: Number(row.sort_order ?? 0),
    notes: String(row.notes ?? ''),
    locked: row.locked === true,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function formatDateTime(value: string): string {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function isoToLocalInput(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function localInputToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function safeSheetName(input: string): string {
  const cleaned = input.replace(/[\\/?*[\]:]/g, ' ').trim();
  return (cleaned || 'Formulare').slice(0, 31);
}

function formatMetricNumber(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
}

function previewFormUrl(slug: string): string {
  if (!slug.trim()) return '';
  return new URL(`/forms/${slug.trim()}`, window.location.origin).toString();
}

function buildCopyTitle(existingTitles: string[], currentTitle: string): string {
  const base = `${currentTitle.trim() || 'Formular'} Kopie`;
  if (!existingTitles.includes(base)) return base;

  let counter = 2;
  while (existingTitles.includes(`${base} ${counter}`)) {
    counter += 1;
  }
  return `${base} ${counter}`;
}

function buildCopySlug(existingSlugs: string[], currentSlug: string): string {
  const base = slugify(`${currentSlug.trim() || 'formular'}-kopie`) || 'formular-kopie';
  if (!existingSlugs.includes(base)) return base;

  let counter = 2;
  while (existingSlugs.includes(`${base}-${counter}`)) {
    counter += 1;
  }
  return `${base}-${counter}`;
}

function groupSubmissions(
  submissions: FormSubmissionRow[],
  answers: FormSubmissionAnswerRow[],
): FormSubmissionWithAnswers[] {
  const answersBySubmission = new Map<string, FormSubmissionAnswerRow[]>();
  for (const answer of answers) {
    const existing = answersBySubmission.get(answer.submission_id) ?? [];
    existing.push(answer);
    answersBySubmission.set(answer.submission_id, existing);
  }

  return submissions.map((submission) => ({
    ...submission,
    answers: (answersBySubmission.get(submission.id) ?? []).slice().sort((a, b) => {
      if (a.field_order !== b.field_order) return a.field_order - b.field_order;
      return a.field_key.localeCompare(b.field_key, 'de');
    }),
  }));
}

function buildExportRows(
  fields: FormFieldRow[],
  submissions: FormSubmissionWithAnswers[],
): Array<Record<string, string>> {
  const orderedKeys = new Map<string, { label: string; order: number }>();

  for (const field of fields.filter((entry) => !isStaticField(entry.field_type))) {
    orderedKeys.set(field.field_key, {
      label: field.label || field.field_key,
      order: field.sort_order,
    });
  }

  for (const submission of submissions) {
    for (const answer of submission.answers) {
      if (!orderedKeys.has(answer.field_key)) {
        orderedKeys.set(answer.field_key, {
          label: answer.field_label || answer.field_key,
          order: answer.field_order,
        });
      }
    }
  }

  const orderedFields = Array.from(orderedKeys.entries())
    .map(([key, meta]) => ({ key, ...meta }))
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.label.localeCompare(b.label, 'de');
    });

  return submissions.map((submission) => {
    const row: Record<string, string> = {
      'Eingang': formatDateTime(submission.submitted_at),
      'Submission ID': submission.id,
      'Quelle': submissionSourceLabel(submission.source),
      'Testeinreichung': submission.test_submission ? 'Ja' : 'Nein',
      'IP Hash': submission.source_ip_hash ?? '',
      'User-Agent': submission.user_agent ?? '',
    };

    const answerMap = new Map(
      submission.answers.map((answer) => [answer.field_key, formatStoredAnswerValue(answer)]),
    );

    for (const field of orderedFields) {
      row[field.label] = answerMap.get(field.key) ?? '';
    }

    return row;
  });
}

function readNumericAnswer(answer: FormSubmissionAnswerRow | undefined): number | null {
  if (!answer) return null;
  if (typeof answer.value_json === 'number' && Number.isFinite(answer.value_json)) {
    return answer.value_json;
  }

  const value = typeof answer.value_text === 'string' ? Number(answer.value_text.trim()) : Number.NaN;
  return Number.isFinite(value) ? value : null;
}

function extractPersonListAnswer(answer: Pick<FormSubmissionAnswerRow, 'value_json' | 'value_text'> | null | undefined): string[] {
  if (!answer) return [];

  const fromJson = normalizePersonListEntries(answer.value_json);
  if (fromJson.length > 0) return fromJson;

  if (typeof answer.value_text !== 'string' || answer.value_text.trim().length === 0) {
    return [];
  }

  return Array.from(
    new Set(
      answer.value_text
        .split('|')
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );
}

function buildPersonListInsights(
  fields: FormFieldRow[],
  submissions: FormSubmissionWithAnswers[],
): PersonListFieldInsight[] {
  const personFields = fields.filter((field) => isPersonListField(field.field_type));

  return personFields
    .map((field) => {
      const nodes = submissions
        .map((submission) => {
          const answer = submission.answers.find((entry) => entry.field_key === field.field_key);
          const names = extractPersonListAnswer(answer);
          const normalizedNames = Array.from(
            new Set(
              names
                .map((name) => normalizePersonMatchToken(name))
                .filter(Boolean),
            ),
          );

          return {
            submission,
            names,
            normalizedNames,
          };
        })
        .filter((entry) => entry.normalizedNames.length > 0);

      if (nodes.length < 2) return null;

      const relatedByIndex = new Map<number, Set<number>>();
      const displayNameByToken = new Map<string, string>();
      const indicesByToken = new Map<string, number[]>();

      nodes.forEach((node, nodeIndex) => {
        node.names.forEach((name) => {
          const token = normalizePersonMatchToken(name);
          if (!token) return;
          if (!displayNameByToken.has(token)) displayNameByToken.set(token, name.trim());
          const next = indicesByToken.get(token) ?? [];
          next.push(nodeIndex);
          indicesByToken.set(token, next);
        });
      });

      for (const indices of indicesByToken.values()) {
        if (indices.length < 2) continue;
        for (const sourceIndex of indices) {
          const existing = relatedByIndex.get(sourceIndex) ?? new Set<number>();
          for (const targetIndex of indices) {
            if (targetIndex !== sourceIndex) existing.add(targetIndex);
          }
          relatedByIndex.set(sourceIndex, existing);
        }
      }

      const visited = new Set<number>();
      const groups: PersonListMatchGroup[] = [];

      nodes.forEach((_, startIndex) => {
        if (visited.has(startIndex) || !relatedByIndex.has(startIndex)) return;

        const queue = [startIndex];
        const component: number[] = [];

        while (queue.length > 0) {
          const currentIndex = queue.shift() as number;
          if (visited.has(currentIndex)) continue;
          visited.add(currentIndex);
          component.push(currentIndex);

          for (const neighbour of relatedByIndex.get(currentIndex) ?? []) {
            if (!visited.has(neighbour)) queue.push(neighbour);
          }
        }

        if (component.length < 2) return;

        const tokenCounts = new Map<string, number>();
        const componentSubmissions = component.map((nodeIndex) => nodes[nodeIndex]);
        componentSubmissions.forEach((node) => {
          node.normalizedNames.forEach((token) => {
            tokenCounts.set(token, (tokenCounts.get(token) ?? 0) + 1);
          });
        });

        const sharedNames = Array.from(tokenCounts.entries())
          .filter(([, count]) => count > 1)
          .sort((a, b) => {
            if (b[1] !== a[1]) return b[1] - a[1];
            return a[0].localeCompare(b[0], 'de');
          })
          .map(([token]) => displayNameByToken.get(token) ?? token);

        groups.push({
          sharedNames,
          submissions: componentSubmissions
            .map((node) => ({
              submission: node.submission,
              names: node.names,
            }))
            .sort((a, b) => new Date(b.submission.submitted_at).getTime() - new Date(a.submission.submitted_at).getTime()),
        });
      });

      if (groups.length === 0) return null;

      return {
        field,
        groups: groups.sort((a, b) => b.submissions.length - a.submissions.length),
      };
    })
    .filter(Boolean) as PersonListFieldInsight[];
}

export function FormsPage() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const [selectedFormId, setSelectedFormId] = useState('');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState('');
  const [submissionDeleteTarget, setSubmissionDeleteTarget] =
    useState<FormSubmissionWithAnswers | null>(null);
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [showTestSubmissions, setShowTestSubmissions] = useState(false);
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>('submissions');
  const [formEditor, setFormEditor] = useState<FormEditorState>(emptyFormEditor());
  const [fieldEditor, setFieldEditor] = useState<FieldEditorState>(emptyFieldEditor());
  const [roomContingentEditor, setRoomContingentEditor] =
    useState<RoomContingentEditorState>(emptyRoomContingentEditor());
  const [roomEditor, setRoomEditor] = useState<RoomEditorState>(emptyRoomEditor());
  const [manualRoomParticipant, setManualRoomParticipant] =
    useState<ManualRoomParticipantState>({ name: '', participantId: null, roomId: '' });
  const [groupRoomSelections, setGroupRoomSelections] = useState<Record<string, string>>({});
  const [jsonImportOpen, setJsonImportOpen] = useState(false);
  const [jsonImportText, setJsonImportText] = useState('');
  const [roomSwapSource, setRoomSwapSource] = useState<string | null>(null);
  const [movePersonId, setMovePersonId] = useState<string | null>(null);
  const [resolvingAssignmentId, setResolvingAssignmentId] = useState<string | null>(null);
  const [resolvingWish, setResolvingWish] = useState<{ name: string; groupKey: string } | null>(null);
  const [dragOverRoomKey, setDragOverRoomKey] = useState<string | null>(null);
  const [pushingGroupKey, setPushingGroupKey] = useState<string | null>(null);
  const [roomSettingsLocal, setRoomSettingsLocal] = useState<{
    enabled: boolean;
    suggestionsEnabled: boolean;
    personListFieldId: string | null;
  } | null>(null);
  const startNewFieldEditor = () => {
    setFieldEditor(emptyFieldEditor(nextFieldSortOrder(fieldsQ.data ?? [])));
  };
  const startNewRoomContingentEditor = () => {
    setRoomContingentEditor(emptyRoomContingentEditor(nextRoomContingentSortOrder(roomContingentsQ.data ?? [])));
  };

  useEffect(() => {
    document.title = 'Formulare – Musical!Kultur Admin';
  }, []);

  const formsQ = useQuery({
    queryKey: ['forms'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FormRow[]> => {
      const { data, error } = await supabase
        .from('forms')
        .select('*')
        .order('updated_at', { ascending: false })
        .order('title', { ascending: true });

      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map(mapFormRow);
    },
  });

  const fieldsQ = useQuery({
    queryKey: ['form-fields', selectedFormId],
    enabled: selectedFormId.trim().length > 0,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<FormFieldRow[]> => {
      const { data, error } = await supabase
        .from('form_fields')
        .select('*')
        .eq('form_id', selectedFormId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map(mapFieldRow);
    },
  });

  // Single embedded query instead of two sequential round-trips.
  const submissionsQ = useQuery({
    queryKey: ['form-submissions', selectedFormId, showTestSubmissions],
    enabled: selectedFormId.trim().length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<FormSubmissionWithAnswers[]> => {
      type RawRow = Record<string, unknown> & { form_submission_answers: Array<Record<string, unknown>> };

      let query = supabase
        .from('form_submissions')
        .select('*, form_submission_answers!submission_id(*)')
        .eq('form_id', selectedFormId)
        .order('submitted_at', { ascending: false });

      if (!showTestSubmissions) {
        query = query.eq('test_submission', false);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data ?? []) as RawRow[];
      const submissions = rows.map((row) => {
        const { form_submission_answers, ...rest } = row;
        void form_submission_answers;
        return mapSubmissionRow(rest as Record<string, unknown>);
      });
      const answers = rows
        .flatMap((r) => r.form_submission_answers ?? [])
        .map(mapSubmissionAnswerRow)
        .sort((a, b) => a.field_order - b.field_order || a.created_at.localeCompare(b.created_at));

      return groupSubmissions(submissions, answers);
    },
  });

  const roomConfigQ = useQuery({
    queryKey: ['form-room-config', selectedFormId],
    enabled: selectedFormId.trim().length > 0,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<FormRoomPlanningConfigRow | null> => {
      const { data, error } = await supabase
        .from('form_room_planning_configs')
        .select('*')
        .eq('form_id', selectedFormId)
        .maybeSingle();

      if (error) throw error;
      return data ? mapRoomPlanningConfigRow(data as Record<string, unknown>) : null;
    },
  });

  const roomContingentsQ = useQuery({
    queryKey: ['form-room-contingents', selectedFormId],
    enabled: selectedFormId.trim().length > 0,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<FormRoomContingentRow[]> => {
      const { data, error } = await supabase
        .from('form_room_contingents')
        .select('*')
        .eq('form_id', selectedFormId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map(mapRoomContingentRow);
    },
  });

  const roomsQ = useQuery({
    queryKey: ['form-rooms', selectedFormId],
    enabled: selectedFormId.trim().length > 0,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<FormRoomRow[]> => {
      const { data, error } = await supabase
        .from('form_rooms')
        .select('*')
        .eq('form_id', selectedFormId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map(mapRoomRow);
    },
  });

  const roomAssignmentsQ = useQuery({
    queryKey: ['form-room-assignments', selectedFormId],
    enabled: selectedFormId.trim().length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<FormRoomAssignmentRow[]> => {
      const { data, error } = await supabase
        .from('form_room_assignments')
        .select('*')
        .eq('form_id', selectedFormId)
        .order('room_id', { ascending: true, nullsFirst: false })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map(mapRoomAssignmentRow);
    },
  });

  const roomProfileSuggestionsQ = useQuery({
    queryKey: ['form-room-profile-suggestions'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FormProfileSuggestion[]> => (
      (await fetchPublicProfiles())
        .map((profile) => normalizeProfileSuggestion(profile as Record<string, unknown>))
        .filter((profile): profile is FormProfileSuggestion => Boolean(profile))
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' }))
    ),
  });

  const deleteSubmissionM = useMutation({
    mutationFn: async (submissionId: string) => {
      const { error } = await supabase.rpc('delete_form_submission', {
        p_submission_id: submissionId,
      });
      if (error) throw error;
    },
    onSuccess: async (_, submissionId) => {
      notifications.show({
        color: 'teal',
        title: 'Gelöscht',
        message: 'Einreichung und Antworten wurden entfernt.',
      });
      if (selectedSubmissionId === submissionId) {
        setSelectedSubmissionId('');
      }
      setSubmissionDeleteTarget(null);
      await qc.invalidateQueries({ queryKey: ['form-submissions', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Löschen fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Einreichung konnte nicht gelöscht werden.',
      });
    },
  });

  const invalidateRoomPlanning = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['form-room-config', selectedFormId] }),
      qc.invalidateQueries({ queryKey: ['form-room-contingents', selectedFormId] }),
      qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] }),
      qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] }),
    ]);
  };

  const saveRoomConfigM = useMutation({
    mutationFn: async (payload: {
      enabled: boolean;
      personListFieldId: string | null;
      suggestionsEnabled: boolean;
    }) => {
      const userId = session?.user?.id;
      if (!selectedFormId || !userId) throw new Error('Formular oder Session fehlt.');

      const { error } = await supabase
        .from('form_room_planning_configs')
        .upsert({
          form_id: selectedFormId,
          enabled: payload.enabled,
          person_list_field_id: payload.personListFieldId,
          suggestions_enabled: payload.suggestionsEnabled,
          updated_by: userId,
        }, { onConflict: 'form_id' });

      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Zimmerplanung gespeichert',
        message: 'Die Einstellungen wurden aktualisiert.',
      });
      setRoomSettingsLocal(null);
      await invalidateRoomPlanning();
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmerplanung fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Einstellungen konnten nicht gespeichert werden.',
      });
    },
  });

  const saveRoomContingentM = useMutation({
    mutationFn: async (): Promise<FormRoomContingentRow> => {
      if (!selectedFormId) throw new Error('Bitte zuerst ein Formular auswählen.');
      const roomCount = Number(roomContingentEditor.roomCount);
      const capacity = Number(roomContingentEditor.capacity);
      const sortOrder = Number(roomContingentEditor.sortOrder);
      if (!Number.isFinite(roomCount) || roomCount < 1) throw new Error('Zimmeranzahl fehlt.');
      if (!Number.isFinite(capacity) || capacity < 1) throw new Error('Bettenanzahl fehlt.');

      const payload = {
        form_id: selectedFormId,
        room_count: Math.round(roomCount),
        capacity: Math.round(capacity),
        label_prefix: roomContingentEditor.labelPrefix.trim(),
        sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
      };

      if (roomContingentEditor.id) {
        const { data, error } = await supabase
          .from('form_room_contingents')
          .update(payload)
          .eq('id', roomContingentEditor.id)
          .select('*')
          .single();
        if (error) throw error;
        return mapRoomContingentRow(data as Record<string, unknown>);
      }

      const { data, error } = await supabase
        .from('form_room_contingents')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return mapRoomContingentRow(data as Record<string, unknown>);
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Zimmermenge gespeichert',
        message: 'Die Zimmerangabe wurde aktualisiert.',
      });
      startNewRoomContingentEditor();
      await qc.invalidateQueries({ queryKey: ['form-room-contingents', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmermenge fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Zimmermenge konnte nicht gespeichert werden.',
      });
    },
  });

  const deleteRoomContingentM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('form_room_contingents').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async (_, id) => {
      if (roomContingentEditor.id === id) startNewRoomContingentEditor();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['form-room-contingents', selectedFormId] }),
        qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] }),
      ]);
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Löschen fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Zimmermenge konnte nicht gelöscht werden.',
      });
    },
  });

  const generateRoomsM = useMutation({
    mutationFn: async () => {
      if (!selectedFormId) throw new Error('Bitte zuerst ein Formular auswählen.');
      const generatedRooms = buildMissingBedroomRoomInserts(
        (roomContingentsQ.data ?? []).map((contingent) => ({
          id: contingent.id,
          count: contingent.room_count,
          capacity: contingent.capacity,
          labelPrefix: contingent.label_prefix,
          sortOrder: contingent.sort_order,
        })),
        roomsQ.data ?? [],
      );
      const inserts: Array<{
        form_id: string;
        contingent_id: string;
        name: string;
        capacity: number;
        floor: string;
        type: string;
        is_sleeping_room: boolean;
        notes: string;
        sort_order: number;
      }> = generatedRooms.map((room) => ({
        form_id: selectedFormId,
        ...room,
      }));

      if (inserts.length === 0) return 0;
      const { error } = await supabase.from('form_rooms').insert(inserts);
      if (error) throw error;
      return inserts.length;
    },
    onSuccess: async (createdCount) => {
      notifications.show({
        color: 'teal',
        title: 'Zimmer erzeugt',
        message: createdCount > 0
          ? `${createdCount} fehlende Zimmer wurden angelegt.`
          : 'Alle Zimmermengen haben bereits ausreichend konkrete Zimmer.',
      });
      await qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmer konnten nicht erzeugt werden',
        message: error instanceof Error ? error.message : 'Bitte Zimmermengen prüfen.',
      });
    },
  });

  const updateRoomM = useMutation({
    mutationFn: async (payload: {
      id: string;
      name: string;
      capacity: number;
      notes?: string;
      is_locked?: boolean;
    }) => {
      const { error } = await supabase
        .from('form_rooms')
        .update({
          name: payload.name,
          capacity: payload.capacity,
          type: 'Schlafraum',
          is_sleeping_room: true,
          ...(payload.notes !== undefined ? { notes: payload.notes } : {}),
          ...(payload.is_locked !== undefined ? { is_locked: payload.is_locked } : {}),
        })
        .eq('id', payload.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmer konnte nicht gespeichert werden',
        message: error instanceof Error ? error.message : 'Bitte Eingabe prüfen.',
      });
    },
  });

  const createRoomM = useMutation({
    mutationFn: async () => {
      if (!selectedFormId) throw new Error('Bitte zuerst ein Formular auswählen.');
      const name = roomEditor.name.trim();
      const capacity = Number(roomEditor.capacity);
      if (!name) throw new Error('Bitte einen Zimmernamen eingeben.');
      if (!Number.isFinite(capacity) || capacity < 1) throw new Error('Bitte eine gültige Bettenzahl eingeben.');

      const { error } = await supabase.from('form_rooms').insert({
        form_id: selectedFormId,
        name,
        capacity: Math.round(capacity),
        floor: '',
        type: 'Schlafraum',
        is_sleeping_room: true,
        notes: roomEditor.notes.trim(),
        sort_order: (roomsQ.data ?? []).length,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Zimmer hinzugefügt',
        message: 'Das Zimmer ist in der Belegung verfügbar.',
      });
      setRoomEditor(emptyRoomEditor());
      await qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmer konnte nicht hinzugefügt werden',
        message: error instanceof Error ? error.message : 'Bitte Eingabe prüfen.',
      });
    },
  });

  const importRoomsFromJsonM = useMutation({
    mutationFn: async (rooms: { label: string; capacity: number }[]) => {
      if (!selectedFormId) throw new Error('Bitte zuerst ein Formular auswählen.');
      const existingNames = new Set((roomsQ.data ?? []).map((r) => r.name.trim().toLowerCase()));
      const inserts = rooms
        .filter((r) => r.label?.trim() && r.capacity >= 1)
        .filter((r) => !existingNames.has(r.label.trim().toLowerCase()))
        .map((r, i) => ({
          form_id: selectedFormId,
          name: r.label.trim(),
          capacity: Math.round(r.capacity),
          floor: '',
          type: 'Schlafraum',
          is_sleeping_room: true,
          notes: '',
          sort_order: (roomsQ.data ?? []).length + i,
        }));
      if (inserts.length === 0) throw new Error('Alle Zimmer sind bereits vorhanden oder die Liste ist leer.');
      const { error } = await supabase.from('form_rooms').insert(inserts);
      if (error) throw error;
      return inserts.length;
    },
    onSuccess: async (count) => {
      notifications.show({
        color: 'teal',
        title: 'Zimmer importiert',
        message: `${count} Zimmer wurden erfolgreich angelegt.`,
      });
      setJsonImportOpen(false);
      setJsonImportText('');
      await qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Import fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Bitte JSON prüfen.',
      });
    },
  });

  const swapRoomsM = useMutation({
    mutationFn: async ({ roomAId, roomBId }: { roomAId: string; roomBId: string }) => {
      const allAssignments = roomAssignmentsQ.data ?? [];
      const inA = allAssignments.filter((a) => a.room_id === roomAId);
      const inB = allAssignments.filter((a) => a.room_id === roomBId);
      const updates = [
        ...inA.map((a) => ({ id: a.id, room_id: roomBId })),
        ...inB.map((a) => ({ id: a.id, room_id: roomAId })),
      ];
      for (const u of updates) {
        const { error } = await supabase.from('form_room_assignments').update({ room_id: u.room_id }).eq('id', u.id);
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      setRoomSwapSource(null);
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: 'Tausch fehlgeschlagen', message: error instanceof Error ? error.message : '' });
    },
  });

  const sendRoomWishReminderM = useMutation({
    mutationFn: async ({ userIds, groupKey }: { userIds: string[]; groupKey: string }) => {
      setPushingGroupKey(groupKey);
      const { data, error } = await supabase.functions.invoke('send-form-room-wish-reminder', {
        body: { user_ids: userIds, form_id: selectedFormId },
      });
      if (error) throw error;
      if (!data || data.ok !== true) {
        const msg = (data as { error?: string } | null)?.error ?? 'Unbekannter Fehler';
        throw new Error(msg);
      }
      return data as { sent_users: number; recipients: number };
    },
    onSuccess: (data) => {
      notifications.show({
        color: 'teal',
        title: 'Push gesendet',
        message: `${data.sent_users} von ${data.recipients} Personen erreicht`,
      });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Push fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
    onSettled: () => setPushingGroupKey(null),
  });

  const deleteRoomM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('form_rooms').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['form-rooms', selectedFormId] }),
        qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] }),
      ]);
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmer konnte nicht gelöscht werden',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  const matchRoomWishNameM = useMutation({
    mutationFn: async (payload: RoomWishNameMatchPayload): Promise<number> => {
      if (!selectedPersonListFieldId) {
        throw new Error('Bitte zuerst ein Wunschpersonen-Feld wählen.');
      }
      const rawName = payload.rawName.trim();
      const normalizedRawName = normalizePersonMatchToken(rawName);
      if (!normalizedRawName) throw new Error('Name fehlt.');

      const profile = (roomProfileSuggestionsQ.data ?? [])
        .find((entry) => entry.userId === payload.userId);
      if (!profile) throw new Error('Profil nicht gefunden.');

      const replacement = {
        displayName: profile.displayName,
        userId: profile.userId,
        source: 'profile' as const,
      };
      const updates: Array<PromiseLike<unknown>> = [];

      for (const submission of submissionsQ.data ?? []) {
        for (const answer of submission.answers) {
          if (answer.field_id !== selectedPersonListFieldId) continue;
          const entriesFromJson = normalizeFormPersonListEntries(answer.value_json);
          const entries = entriesFromJson.length > 0
            ? entriesFromJson
            : splitPersonListText(answer.value_text).map((displayName) => ({
                displayName,
                userId: null,
                source: 'manual' as const,
              }));
          if (entries.length === 0) continue;

          let changed = false;
          const nextEntries = entries.map((entry) => {
            if (normalizePersonMatchToken(entry.displayName) !== normalizedRawName) {
              return entry;
            }
            if (entry.userId === replacement.userId && entry.displayName === replacement.displayName) {
              return entry;
            }
            changed = true;
            return replacement;
          });
          if (!changed) continue;

          updates.push(
            supabase
              .from('form_submission_answers')
              .update({
                value_json: normalizeFormPersonListEntries(nextEntries),
                value_text: nextEntries.map((entry) => entry.displayName).join(' | '),
              })
              .eq('submission_id', answer.submission_id)
              .eq('field_id', answer.field_id ?? '')
              .then(({ error }) => {
                if (error) throw error;
              }),
          );
        }
      }

      await Promise.all(updates);
      return updates.length;
    },
    onSuccess: async (count, payload) => {
      notifications.show({
        color: count > 0 ? 'teal' : 'yellow',
        title: count > 0 ? 'Name zugeordnet' : 'Keine Antwort geändert',
        message: count > 0
          ? `„${payload.rawName}" wurde in ${count} Antwort${count === 1 ? '' : 'en'} als Profil gespeichert. Berechne den Vorschlag danach neu.`
          : 'Für diesen Namen wurde keine passende Antwort gefunden.',
      });
      await qc.invalidateQueries({ queryKey: ['form-submissions', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zuordnung fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Name konnte nicht zugeordnet werden.',
      });
    },
  });

  const saveProposalM = useMutation({
    mutationFn: async () => {
      const config = roomConfigQ.data;
      if (!selectedFormId || !config?.person_list_field_id) {
        throw new Error('Bitte zuerst Zimmerplanung aktivieren und ein Wunschpersonen-Feld wählen.');
      }
      const participants = buildRoomPlanningParticipants(
        submissionsQ.data ?? [],
        config.person_list_field_id,
      );
      const proposal = buildRoomProposal(
        participants,
        bedroomRooms,
        (roomAssignmentsQ.data ?? []).filter((assignment) => assignment.locked),
      );

      const { error } = await supabase.rpc('replace_form_room_assignments', {
        p_form_id: selectedFormId,
        p_assignments: proposal.assignments.map((entry, index) => ({
          room_id: entry.roomId,
          submission_id: entry.participant.submission.id,
          user_id: entry.participant.userId,
          display_name: entry.participant.displayName,
          source: entry.participant.userId ? 'profile' : 'submission',
          sort_order: index,
          notes: entry.notes,
          locked: false,
        })),
      });
      if (error) throw error;
      return proposal.assignments.length;
    },
    onSuccess: async (count) => {
      notifications.show({
        color: 'teal',
        title: 'Vorschlag gespeichert',
        message: `${count} Personen wurden in die Zimmerplanung übernommen.`,
      });
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Vorschlag fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Zimmerplan konnte nicht berechnet werden.',
      });
    },
  });

  const updateAssignmentRoomM = useMutation({
    mutationFn: async (payload: { id: string; roomId: string | null }) => {
      const { error } = await supabase
        .from('form_room_assignments')
        .update({ room_id: payload.roomId })
        .eq('id', payload.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zimmerzuordnung fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  const toggleAssignmentLockedM = useMutation({
    mutationFn: async (payload: { id: string; locked: boolean }) => {
      const { error } = await supabase
        .from('form_room_assignments')
        .update({ locked: payload.locked })
        .eq('id', payload.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Fixierung konnte nicht gespeichert werden',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  const assignGroupToRoomM = useMutation({
    mutationFn: async (payload: { participantIds: string[]; roomId: string; groupKey: string }) => {
      if (!selectedFormId) throw new Error('Kein Formular ausgewählt.');
      const existing = roomAssignmentsQ.data ?? [];
      const identityIndex = buildRoomPlanningParticipantIdentityIndex(roomPlanningParticipants);
      const existingByParticipantId = new Map<string, FormRoomAssignmentRow>();
      for (const assignment of existing) {
        const participant = resolveRoomPlanningAssignmentParticipant(assignment, identityIndex);
        if (participant) {
          existingByParticipantId.set(roomPlanningParticipantKey(participant), assignment);
        }
      }
      const participants = payload.participantIds
        .map((participantId) => identityIndex.bySubmissionId.get(participantId))
        .filter((participant): participant is RoomPlanningParticipant => Boolean(participant));

      const toInsert: RoomPlanningParticipant[] = [];
      for (const participant of participants) {
        const assignment = existingByParticipantId.get(roomPlanningParticipantKey(participant));
        if (!assignment) {
          toInsert.push(participant);
          continue;
        }
        const { error } = await supabase
          .from('form_room_assignments')
          .update({
            room_id: payload.roomId || null,
            submission_id: participant.submission.id,
            user_id: participant.userId,
            display_name: participant.displayName,
            source: participant.userId ? 'profile' : 'submission',
          })
          .eq('id', assignment.id);
        if (error) throw error;
      }
      if (toInsert.length > 0) {
        const { error } = await supabase.from('form_room_assignments').insert(
          toInsert.map((participant, i) => ({
            form_id: selectedFormId,
            room_id: payload.roomId || null,
            submission_id: participant.submission.id,
            user_id: participant.userId,
            display_name: participant.displayName,
            source: participant.userId ? 'profile' : 'submission',
            sort_order: existing.length + i,
            locked: false,
          })),
        );
        if (error) throw error;
      }
    },
    onSuccess: async (_data, variables) => {
      notifications.show({
        color: 'teal',
        title: 'Gruppe zugewiesen',
        message: 'Alle Personen wurden dem Zimmer zugeordnet.',
      });
      setGroupRoomSelections((prev) => ({ ...prev, [variables.groupKey]: '' }));
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zuweisung fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  const addManualAssignmentM = useMutation({
    mutationFn: async () => {
      const name = manualRoomParticipant.name.trim();
      if (!selectedFormId || !name) throw new Error('Bitte einen Namen eingeben.');
      const participant = manualRoomParticipant.participantId
        ? participantBySubmissionId.get(manualRoomParticipant.participantId)
        : participantByUniqueName.get(normalizePersonMatchToken(name));
      const { error } = await supabase.from('form_room_assignments').insert({
        form_id: selectedFormId,
        room_id: manualRoomParticipant.roomId || null,
        submission_id: participant?.submission.id ?? null,
        user_id: participant?.userId ?? null,
        display_name: participant?.displayName ?? name,
        source: participant ? (participant.userId ? 'profile' : 'submission') : 'external',
        sort_order: (roomAssignmentsQ.data ?? []).length,
        locked: true,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Person hinzugefügt',
        message: 'Die Person wurde zur Belegung ergänzt.',
      });
      setManualRoomParticipant({ name: '', participantId: null, roomId: '' });
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Person konnte nicht ergänzt werden',
        message: error instanceof Error ? error.message : 'Bitte Eingabe prüfen.',
      });
    },
  });

  const deleteAssignmentM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('form_room_assignments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Person konnte nicht entfernt werden',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  // Resolve an unlinked assignment row by linking it to a known user profile.
  // This sets user_id + source='profile' directly on form_room_assignments so the
  // app can show the room assignment to the correct user even before they submit.
  const resolveAssignmentUserM = useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const existing = roomAssignmentsQ.data ?? [];
      const participant = roomParticipantIdentityIndex.byUserId.get(userId);
      const duplicate = existing.find((assignment) => {
        if (assignment.id === id) return false;
        if (assignment.user_id === userId) return true;
        if (!participant) return false;
        const assignmentParticipant = resolveRoomPlanningAssignmentParticipant(assignment, roomParticipantIdentityIndex);
        return assignmentParticipant?.submission.id === participant.submission.id;
      });
      if (duplicate) {
        throw new Error(`Dieser Benutzer ist bereits als „${duplicate.display_name}" in der Belegung eingetragen.`);
      }
      const profile = (roomProfileSuggestionsQ.data ?? []).find((p) => p.userId === userId);
      const { error } = await supabase
        .from('form_room_assignments')
        .update({
          user_id: userId,
          submission_id: participant?.submission.id ?? null,
          ...(participant || profile ? { display_name: participant?.displayName ?? profile!.displayName } : {}),
          source: 'profile',
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'Zugeordnet', message: 'Person wurde mit App-Profil verknüpft.' });
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Zuordnung fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  // Mark an unlinked assignment as external — person has no app account.
  const resolveAssignmentExternalM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('form_room_assignments')
        .update({ source: 'external' })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({ color: 'indigo', title: 'Als extern markiert', message: 'Person wird nicht über die App benachrichtigt.' });
      await qc.invalidateQueries({ queryKey: ['form-room-assignments', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Konnte nicht als extern markiert werden',
        message: error instanceof Error ? error.message : 'Bitte erneut versuchen.',
      });
    },
  });

  const selectedForm = useMemo(
    () => (formsQ.data ?? []).find((form) => form.id === selectedFormId) ?? null,
    [formsQ.data, selectedFormId],
  );

  useEffect(() => {
    if (isCreatingForm) return;
    if (selectedFormId || (formsQ.data ?? []).length === 0) return;
    setSelectedFormId((formsQ.data ?? [])[0].id);
  }, [formsQ.data, isCreatingForm, selectedFormId]);

  useEffect(() => {
    if (!selectedForm) return;
    setFormEditor({
      id: selectedForm.id,
      title: selectedForm.title,
      slug: selectedForm.slug,
      shortDescription: selectedForm.short_description,
      introText: selectedForm.intro_text,
      status: selectedForm.status,
      startsAt: isoToLocalInput(selectedForm.starts_at),
      endsAt: isoToLocalInput(selectedForm.ends_at),
      showOnHomepage: selectedForm.show_on_homepage,
      homepageTitle: selectedForm.homepage_title ?? '',
      homepageText: selectedForm.homepage_text ?? '',
      homepageCtaLabel: selectedForm.homepage_cta_label ?? '',
      pushBody: resolveFormPushBody(selectedForm),
      pushBodyTouched: Boolean(selectedForm.push_body?.trim()),
      submitButtonLabel: selectedForm.submit_button_label,
      confirmationTitle: selectedForm.confirmation_title,
      confirmationMessage: selectedForm.confirmation_message,
      allowSubmissionEdit: selectedForm.allow_submission_edit,
    });
    setSlugTouched(true);
    setIsCreatingForm(false);
  }, [selectedForm]);

  useEffect(() => {
    const submissions = submissionsQ.data ?? [];
    if (submissions.length === 0) {
      if (selectedSubmissionId) setSelectedSubmissionId('');
      return;
    }
    if (!selectedSubmissionId || !submissions.some((entry) => entry.id === selectedSubmissionId)) {
      setSelectedSubmissionId(submissions[0].id);
    }
  }, [selectedSubmissionId, submissionsQ.data]);

  const selectedSubmission = useMemo(
    () => (submissionsQ.data ?? []).find((submission) => submission.id === selectedSubmissionId) ?? null,
    [selectedSubmissionId, submissionsQ.data],
  );

  const filteredSubmissions = useMemo(() => {
    if (analysisTab !== 'submissions') return [];
    const query = submissionSearch.trim().toLowerCase();
    const submissions = submissionsQ.data ?? [];
    if (!query) return submissions;

    return submissions.filter((submission) => {
      const haystack = [
        submission.id,
        submission.source,
        submission.source_ip_hash ?? '',
        submission.user_agent ?? '',
        buildSubmissionPreview(submission),
        ...submission.answers.map((answer) => `${answer.field_label} ${formatStoredAnswerValue(answer)}`),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [analysisTab, submissionSearch, submissionsQ.data]);

  const selectionStats = useMemo((): SelectionFieldStats[] => {
    if (analysisTab !== 'stats') return [];
    const fields = (fieldsQ.data ?? []).filter((field) => field.field_type === 'checkbox' || isSelectionField(field.field_type));
    const submissions = submissionsQ.data ?? [];

    return fields
      .map((field) => {
        const counts = new Map<string, number>();
        let answeredCount = 0;

        for (const submission of submissions) {
          const answer = submission.answers.find((entry) => entry.field_key === field.field_key);
          if (!answer) continue;

          if (field.field_type === 'checkbox') {
            const label = answer.value_json === true ? 'Ja' : 'Nein';
            answeredCount += 1;
            counts.set(label, (counts.get(label) ?? 0) + 1);
            continue;
          }

          if (Array.isArray(answer.value_json)) {
            if (answer.value_json.length > 0) answeredCount += 1;
            for (const entry of answer.value_json) {
              const label = String(entry).trim();
              if (!label) continue;
              counts.set(label, (counts.get(label) ?? 0) + 1);
            }
            continue;
          }

          const label = formatStoredAnswerValue(answer);
          if (!label) continue;
          answeredCount += 1;
          counts.set(label, (counts.get(label) ?? 0) + 1);
        }

        if (counts.size === 0) return null;

        return {
          field,
          answeredCount,
          entries: Array.from(counts.entries()).sort((a, b) => {
            if (b[1] !== a[1]) return b[1] - a[1];
            return a[0].localeCompare(b[0], 'de');
          }).map(([label, count]) => ({
            label,
            count,
            share: answeredCount > 0 ? count / answeredCount : 0,
          })),
        };
      })
      .filter(Boolean) as SelectionFieldStats[];
  }, [analysisTab, fieldsQ.data, submissionsQ.data]);

  const numericStats = useMemo((): NumericFieldStats[] => {
    if (analysisTab !== 'stats') return [];
    const fields = (fieldsQ.data ?? []).filter((field) => field.field_type === 'number');
    const submissions = submissionsQ.data ?? [];

    return fields
      .map((field) => {
        const values = submissions
          .map((submission) => readNumericAnswer(submission.answers.find((entry) => entry.field_key === field.field_key)))
          .filter((value): value is number => value !== null);

        if (values.length === 0) return null;

        const sum = values.reduce((total, value) => total + value, 0);
        return {
          field,
          answeredCount: values.length,
          average: sum / values.length,
          min: Math.min(...values),
          max: Math.max(...values),
        };
      })
      .filter(Boolean) as NumericFieldStats[];
  }, [analysisTab, fieldsQ.data, submissionsQ.data]);

  const personListInsights = useMemo(
    () => analysisTab === 'stats' ? buildPersonListInsights(fieldsQ.data ?? [], submissionsQ.data ?? []) : [],
    [analysisTab, fieldsQ.data, submissionsQ.data],
  );

  const personListFieldOptions = useMemo(
    () => (fieldsQ.data ?? [])
      .filter((field) => isPersonListField(field.field_type))
      .map((field) => ({
        value: field.id,
        label: field.label || field.field_key,
      })),
    [fieldsQ.data],
  );
  const hasRoomPlanningField = personListFieldOptions.length > 0;

  useEffect(() => {
    if (analysisTab === 'rooms' && !hasRoomPlanningField) {
      setAnalysisTab('submissions');
    }
  }, [analysisTab, hasRoomPlanningField]);

  const selectedPersonListFieldId =
    roomConfigQ.data?.person_list_field_id
    ?? personListFieldOptions[0]?.value
    ?? null;

  const bedroomRooms = useMemo(
    () => normalizeBedroomRooms(roomsQ.data ?? []),
    [roomsQ.data],
  );

  const roomOptions = useMemo(
    () => [
      { value: '', label: 'Nicht zugeordnet' },
      ...bedroomRooms
        .filter((room) => !room.is_locked)
        .map((room) => ({
          value: room.id,
          label: `${room.name} (${room.capacity} Betten)`,
        })),
    ],
    [bedroomRooms],
  );

  const assignableRoomOptions = useMemo(
    () => roomOptions.filter((option) => option.value !== ''),
    [roomOptions],
  );

  const hasMissingRooms = useMemo(() => {
    const contingents = roomContingentsQ.data ?? [];
    const rooms = roomsQ.data ?? [];
    return contingents.some(
      (contingent) => rooms.filter((room) => room.contingent_id === contingent.id).length < contingent.room_count,
    );
  }, [roomContingentsQ.data, roomsQ.data]);

  const roomById = useMemo(() => {
    const map = new Map<string, FormRoomRow>();
    for (const room of bedroomRooms) map.set(room.id, room);
    return map;
  }, [bedroomRooms]);

  const roomPlanningParticipants = useMemo(
    () => buildRoomPlanningParticipants(submissionsQ.data ?? [], selectedPersonListFieldId),
    [selectedPersonListFieldId, submissionsQ.data],
  );

  const roomParticipantIdentityIndex = useMemo(
    () => buildRoomPlanningParticipantIdentityIndex(roomPlanningParticipants),
    [roomPlanningParticipants],
  );

  const proposalDisabledReason = useMemo(() => {
    if (!roomConfigQ.data?.enabled) return 'Zimmerplanung ist nicht aktiv.';
    if (personListFieldOptions.length === 0 || !selectedPersonListFieldId) {
      return 'Es fehlt ein Feld für Zimmerwünsche/Wunschpersonen.';
    }
    if (bedroomRooms.length === 0) return 'Es gibt noch kein Schlafzimmer.';
    if ((submissionsQ.data ?? []).length === 0) return 'Es gibt noch keine Einreichungen.';
    if (roomPlanningParticipants.length === 0) return 'Aus den Einreichungen konnten keine Personen gelesen werden.';
    return '';
  }, [
    personListFieldOptions.length,
    roomConfigQ.data?.enabled,
    roomPlanningParticipants.length,
    selectedPersonListFieldId,
    bedroomRooms.length,
    submissionsQ.data,
  ]);

  const participantBySubmissionId = roomParticipantIdentityIndex.bySubmissionId;
  const participantByUniqueName = roomParticipantIdentityIndex.byUniqueName;

  const assignmentsByRoom = useMemo(() => {
    const map = new Map<string, FormRoomAssignmentRow[]>();
    for (const assignment of roomAssignmentsQ.data ?? []) {
      const key = assignment.room_id ?? '';
      const existing = map.get(key) ?? [];
      existing.push(assignment);
      map.set(key, existing);
    }
    return map;
  }, [roomAssignmentsQ.data]);

  const roomPlanExportRows = useMemo(() => {
    const sourceLabels: Record<FormRoomAssignmentRow['source'], string> = {
      submission: 'Einreichung',
      profile: 'Profil',
      manual: 'Manuell',
      external: 'Extern',
    };
    const rows: Array<Record<string, string>> = [];
    const assignments = roomAssignmentsQ.data ?? [];
    for (const assignment of assignments) {
      const room = assignment.room_id ? roomById.get(assignment.room_id) : null;
      const roomEntryCount = room ? (assignmentsByRoom.get(room.id) ?? []).length : 0;
      const participant = resolveRoomPlanningAssignmentParticipant(assignment, roomParticipantIdentityIndex);
      rows.push({
        Zimmer: room?.name ?? 'Nicht zugeordnet',
        Betten: room ? String(room.capacity) : '',
        Belegt: room ? String(roomEntryCount) : '',
        'Freie Plätze': room ? String(Math.max(0, room.capacity - roomEntryCount)) : '',
        Überbelegt: room && roomEntryCount > room.capacity ? 'Ja' : 'Nein',
        Zimmernotiz: room?.notes ?? '',
        Name: assignment.display_name,
        Quelle: sourceLabels[assignment.source] ?? assignment.source,
        Fixiert: assignment.locked ? 'Ja' : 'Nein',
        'Submission ID': participant?.submission.id ?? assignment.submission_id ?? '',
        Wunschliste: participant?.requestedRoommates.join(' | ') ?? '',
        'Bevorzugte Zimmergröße': participant?.preferredRoomSize?.label ?? '',
        Einzelzimmerwunsch: participant?.wantsSingleRoom ? 'Ja' : 'Nein',
        Personenhinweis: participant?.notes ?? '',
        Status: assignment.room_id ? 'Zugeordnet' : 'Nicht zugeordnet',
        Hinweis: assignment.notes,
      });
    }
    return rows;
  }, [assignmentsByRoom, roomAssignmentsQ.data, roomById, roomParticipantIdentityIndex]);

  const roomPlanningConflicts = useMemo(
    () => buildRoomPlanningConflicts(
      roomPlanningParticipants,
      bedroomRooms,
      roomAssignmentsQ.data ?? [],
    ),
    [bedroomRooms, roomAssignmentsQ.data, roomPlanningParticipants],
  );

  const criticalConflicts = useMemo(
    () => roomPlanningConflicts.filter((c) => roomPlanningConflictColor(c.type) === 'red'),
    [roomPlanningConflicts],
  );

  const warningConflicts = useMemo(
    () => roomPlanningConflicts.filter((c) => roomPlanningConflictColor(c.type) === 'yellow'),
    [roomPlanningConflicts],
  );

  const unresolvedWishNames = useMemo(() => {
    const map = new Map<string, { rawName: string; count: number; requestedBy: Set<string> }>();
    for (const conflict of roomPlanningConflicts) {
      if (conflict.type !== 'unmatched_wish' && conflict.type !== 'ambiguous_wish') continue;
      const rawName = conflict.names[1]?.trim();
      const normalizedName = normalizePersonMatchToken(rawName ?? '');
      if (!rawName || !normalizedName) continue;
      const entry = map.get(normalizedName) ?? { rawName, count: 0, requestedBy: new Set<string>() };
      entry.count += 1;
      entry.requestedBy.add(conflict.label);
      map.set(normalizedName, entry);
    }
    return Array.from(map.values())
      .sort((a, b) => a.rawName.localeCompare(b.rawName, 'de', { sensitivity: 'base' }))
      .map((entry) => ({
        rawName: entry.rawName,
        count: entry.count,
        requestedBy: Array.from(entry.requestedBy),
      }));
  }, [roomPlanningConflicts]);

  const profileSelectOptions = useMemo(
    () => (roomProfileSuggestionsQ.data ?? []).map((profile) => ({
      value: profile.userId,
      label: profile.nickname
        ? `${profile.displayName} (${profile.nickname})`
        : profile.displayName,
    })),
    [roomProfileSuggestionsQ.data],
  );

  const assignmentsByParticipantId = useMemo(() => {
    const map = new Map<string, FormRoomAssignmentRow[]>();
    for (const assignment of roomAssignmentsQ.data ?? []) {
      const participant = resolveRoomPlanningAssignmentParticipant(assignment, roomParticipantIdentityIndex);
      if (!participant) continue;
      const participantId = roomPlanningParticipantKey(participant);
      const entries = map.get(participantId) ?? [];
      entries.push(assignment);
      map.set(participantId, entries);
    }
    return map;
  }, [roomAssignmentsQ.data, roomParticipantIdentityIndex]);

  // Assignments that don't correspond to a known submission participant.
  // Linked app profiles are valid app users, but still have no matching form submission.
  const unmatchedRoomAssignments = useMemo(() => {
    return (roomAssignmentsQ.data ?? []).filter(
      (assignment) => !resolveRoomPlanningAssignmentParticipant(assignment, roomParticipantIdentityIndex),
    );
  }, [roomAssignmentsQ.data, roomParticipantIdentityIndex]);

  const appProfileRoomAssignments = useMemo(
    () => unmatchedRoomAssignments.filter((assignment) => assignment.source === 'profile' && Boolean(assignment.user_id)),
    [unmatchedRoomAssignments],
  );

  const extraRoomAssignments = useMemo(
    () => unmatchedRoomAssignments.filter((assignment) => assignment.source !== 'profile' || !assignment.user_id),
    [unmatchedRoomAssignments],
  );

  const participantSelectOptions = useMemo(
    () => roomPlanningParticipants.map((p) => ({ value: p.submission.id, label: p.displayName })),
    [roomPlanningParticipants],
  );

  const roomWishGroupSummaries = useMemo(
    () => buildRoomWishGroupSummaries(roomPlanningParticipants),
    [roomPlanningParticipants],
  );

  const noWishCount = useMemo(
    () => countParticipantsWithoutWishes(roomPlanningParticipants),
    [roomPlanningParticipants],
  );

  const roomWarningsByRoomName = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const conflict of roomPlanningConflicts) {
      if (!['invalid_room', 'over_capacity'].includes(conflict.type)) continue;
      const entries = map.get(conflict.label) ?? [];
      entries.push(conflict.detail);
      map.set(conflict.label, entries);
    }
    return map;
  }, [roomPlanningConflicts]);

  const missingRoomParticipants = useMemo(() => {
    const assignedWithRoom = (roomAssignmentsQ.data ?? []).filter((a) => a.room_id);
    const assignedParticipantIds = new Set(
      assignedWithRoom
        .map((assignment) => resolveRoomPlanningAssignmentParticipant(assignment, roomParticipantIdentityIndex))
        .filter((participant): participant is RoomPlanningParticipant => Boolean(participant))
        .map(roomPlanningParticipantKey),
    );
    return roomPlanningParticipants.filter((p) =>
      !assignedParticipantIds.has(roomPlanningParticipantKey(p)),
    );
  }, [roomAssignmentsQ.data, roomParticipantIdentityIndex, roomPlanningParticipants]);

  const missingRoomParticipantIds = useMemo(
    () => new Set(missingRoomParticipants.map(roomPlanningParticipantKey)),
    [missingRoomParticipants],
  );

  const assignmentProgress = useMemo(() => {
    const assignments = roomAssignmentsQ.data ?? [];
    const assignedWithRoom = assignments.filter((a) => a.room_id);
    const assignedParticipantIds = new Set<string>();
    let appProfileAssigned = 0;
    let externalAssigned = 0;
    for (const assignment of assignedWithRoom) {
      const participant = resolveRoomPlanningAssignmentParticipant(assignment, roomParticipantIdentityIndex);
      if (participant) {
        assignedParticipantIds.add(roomPlanningParticipantKey(participant));
      } else if (assignment.source === 'profile' && assignment.user_id) {
        appProfileAssigned += 1;
      } else {
        externalAssigned += 1;
      }
    }
    const submissionAssigned = roomPlanningParticipants.filter((p) =>
      assignedParticipantIds.has(roomPlanningParticipantKey(p)),
    ).length;
    const total = roomPlanningParticipants.length;
    const assigned = submissionAssigned + appProfileAssigned + externalAssigned;
    return { assigned, total, appProfiles: appProfileAssigned, external: externalAssigned };
  }, [roomAssignmentsQ.data, roomParticipantIdentityIndex, roomPlanningParticipants]);

  const roomPlanningConflictExportRows = useMemo(
    () => roomPlanningConflicts.map((conflict) => ({
      Typ: roomPlanningConflictLabel(conflict.type),
      Bereich: conflict.label,
      Hinweis: conflict.detail,
      Personen: conflict.names.join(' | '),
    })),
    [roomPlanningConflicts],
  );

  useEffect(() => {
    setRoomContingentEditor(emptyRoomContingentEditor());
    setManualRoomParticipant({ name: '', participantId: null, roomId: '' });
    setRoomSettingsLocal(null);
  }, [selectedFormId]);

  useEffect(() => {
    if (roomConfigQ.isLoading) return;
    setRoomSettingsLocal((prev) => prev ?? {
      enabled: roomConfigQ.data?.enabled ?? false,
      suggestionsEnabled: roomConfigQ.data?.suggestions_enabled ?? true,
      personListFieldId: roomConfigQ.data?.person_list_field_id ?? personListFieldOptions[0]?.value ?? null,
    });
  }, [roomConfigQ.data, roomConfigQ.isLoading, personListFieldOptions]);

  const currentPublicUrl = previewFormUrl(formEditor.slug);
  const editorAvailabilityState = useMemo(
    () => getFormAvailabilityState({
      status: formEditor.status,
      starts_at: localInputToIso(formEditor.startsAt),
      ends_at: localInputToIso(formEditor.endsAt),
    }),
    [formEditor.endsAt, formEditor.startsAt, formEditor.status],
  );
  const resolvedFormPushBody = useMemo(
    () => clipFormPushText(formEditor.pushBody.trim() || buildDefaultFormPushBody(formEditor.title)),
    [formEditor.pushBody, formEditor.title],
  );
  const willPublishLiveFromDraft = useMemo(() => {
    if (!formEditor.id || !selectedForm) return false;
    return selectedForm.status === 'draft'
      && formEditor.status === 'published'
      && formEditor.showOnHomepage
      && editorAvailabilityState === 'live'
      && !selectedForm.notification_sent_at;
  }, [editorAvailabilityState, formEditor.id, formEditor.showOnHomepage, formEditor.status, selectedForm]);

  const generateFormPushBodyM = useMutation({
    mutationFn: async (): Promise<string> => {
      const title = formEditor.title.trim();
      if (!title) throw new Error('Bitte zuerst einen Titel für das Formular eingeben.');

      const { data, error } = await supabase.functions.invoke('admin-ai', {
        body: {
          task: 'form_push_body',
          title,
          shortDescription: formEditor.shortDescription.trim(),
          introText: formEditor.introText.trim(),
          homepageText: formEditor.homepageText.trim(),
        },
      });

      if (error) throw error;

      const response = (data ?? {}) as {
        ok?: boolean;
        body?: string;
        error?: string;
      };

      if (response.ok !== true || !response.body) {
        throw new Error(response.error || 'KI-Text konnte nicht generiert werden.');
      }

      return response.body;
    },
    onSuccess: (body) => {
      setFormEditor((prev) => ({
        ...prev,
        pushBody: body,
        pushBodyTouched: true,
      }));
      notifications.show({
        color: 'teal',
        title: 'KI-Text erzeugt',
        message: 'Der Push-Text wurde als Vorschlag eingefügt.',
      });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'KI-Text fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Der Push-Text konnte nicht erzeugt werden.',
      });
    },
  });

  const saveFormM = useMutation({
    mutationFn: async (
      options?: { suppressPushOnce?: boolean },
    ): Promise<FormRow> => {
      const userId = session?.user?.id;
      if (!userId) throw new Error('Session fehlt.');

      const title = formEditor.title.trim();
      const slug = formEditor.slug.trim().toLowerCase();
      if (!title) throw new Error('Titel fehlt.');
      if (!slug) throw new Error('Slug fehlt.');

      const startsAt = localInputToIso(formEditor.startsAt);
      const endsAt = localInputToIso(formEditor.endsAt);
      const defaultPushBody = clipFormPushText(buildDefaultFormPushBody(title));
      const normalizedPushBody = clipFormPushText(formEditor.pushBody.trim());
      if (startsAt && endsAt && new Date(startsAt).getTime() > new Date(endsAt).getTime()) {
        throw new Error('Start darf nicht nach dem Ende liegen.');
      }

      const payload = {
        title,
        slug,
        short_description: formEditor.shortDescription.trim(),
        intro_text: formEditor.introText.trim(),
        submit_button_label: formEditor.submitButtonLabel.trim() || 'Absenden',
        confirmation_title: formEditor.confirmationTitle.trim() || 'Vielen Dank',
        confirmation_message: formEditor.confirmationMessage.trim() || 'Deine Anmeldung wurde gespeichert.',
        allow_submission_edit: formEditor.allowSubmissionEdit,
        status: formEditor.status,
        starts_at: startsAt,
        ends_at: endsAt,
        show_on_homepage: formEditor.showOnHomepage,
        homepage_title: formEditor.homepageTitle.trim() || null,
        homepage_text: formEditor.homepageText.trim() || null,
        homepage_cta_label: formEditor.homepageCtaLabel.trim() || null,
        push_body: normalizedPushBody && normalizedPushBody !== defaultPushBody ? normalizedPushBody : null,
        suppress_push_once: options?.suppressPushOnce === true,
        public_url: currentPublicUrl,
        updated_by: userId,
      };

      if (formEditor.id) {
        const { data, error } = await supabase
          .from('forms')
          .update(payload)
          .eq('id', formEditor.id)
          .select('*')
          .single();

        if (error) throw error;
        return mapFormRow(data as Record<string, unknown>);
      }

      const { data, error } = await supabase
        .from('forms')
        .insert({
          ...payload,
          created_by: userId,
        })
        .select('*')
        .single();

      if (error) throw error;
      return mapFormRow(data as Record<string, unknown>);
    },
    onSuccess: async (form, options) => {
      const availability = getFormAvailabilityState(form);
      const publishMessage =
        form.status !== 'published'
          ? 'Formular aktualisiert.'
          : availability === 'live'
            ? 'Formular ist jetzt öffentlich erreichbar.'
            : availability === 'scheduled'
              ? 'Formular ist veröffentlicht, wird aber erst zum Startzeitpunkt öffentlich.'
              : availability === 'expired'
                ? 'Formular ist veröffentlicht, das Aktivitätsfenster ist jedoch bereits abgelaufen.'
                : 'Formular wurde veröffentlicht.';

      notifications.show({
        color: 'teal',
        title: 'Gespeichert',
        message: options?.suppressPushOnce === true && willPublishLiveFromDraft
          ? 'Formular gespeichert. Die Push wurde für diesen Speichervorgang nicht gesendet.'
          : formEditor.id
            ? publishMessage
            : form.status === 'published'
              ? publishMessage
              : 'Formular angelegt.',
      });
      setIsCreatingForm(false);
      setSelectedFormId(form.id);
      setSlugTouched(true);
      await qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Speichern fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Formular konnte nicht gespeichert werden.',
      });
    },
  });

  const deleteFormM = useMutation({
    mutationFn: async (formId: string) => {
      const { error } = await supabase.from('forms').delete().eq('id', formId);
      if (error) throw error;
    },
    onSuccess: async (_, formId) => {
      notifications.show({
        color: 'teal',
        title: 'Gelöscht',
        message: 'Formular und zugehörige Daten wurden entfernt.',
      });
      if (selectedFormId === formId) {
        setSelectedFormId('');
        setIsCreatingForm(false);
        setFormEditor(emptyFormEditor());
        setFieldEditor(emptyFieldEditor());
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['forms'] }),
        qc.invalidateQueries({ queryKey: ['form-fields'] }),
        qc.invalidateQueries({ queryKey: ['form-submissions'] }),
      ]);
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Löschen fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Formular konnte nicht gelöscht werden.',
      });
    },
  });

  const duplicateFormM = useMutation({
    mutationFn: async (sourceForm: FormRow): Promise<FormRow> => {
      const userId = session?.user?.id;
      if (!userId) throw new Error('Session fehlt.');

      const existingForms = formsQ.data ?? [];
      const nextTitle = buildCopyTitle(existingForms.map((form) => form.title), sourceForm.title);
      const nextSlug = buildCopySlug(existingForms.map((form) => form.slug), sourceForm.slug);
      const nextPublicUrl = previewFormUrl(nextSlug);

      const { data: sourceFieldsRaw, error: sourceFieldsError } = await supabase
        .from('form_fields')
        .select('*')
        .eq('form_id', sourceForm.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (sourceFieldsError) throw sourceFieldsError;

      let createdFormId = '';

      try {
        const { data: createdFormRaw, error: createdFormError } = await supabase
          .from('forms')
          .insert({
            title: nextTitle,
            slug: nextSlug,
            short_description: sourceForm.short_description,
            intro_text: sourceForm.intro_text,
            submit_button_label: sourceForm.submit_button_label,
            confirmation_title: sourceForm.confirmation_title,
            confirmation_message: sourceForm.confirmation_message,
            allow_submission_edit: sourceForm.allow_submission_edit,
            status: 'draft',
            starts_at: null,
            ends_at: null,
            show_on_homepage: false,
            homepage_title: sourceForm.homepage_title,
            homepage_text: sourceForm.homepage_text,
            homepage_cta_label: sourceForm.homepage_cta_label,
            push_body: sourceForm.push_body,
            public_url: nextPublicUrl,
            created_by: userId,
            updated_by: userId,
          })
          .select('*')
          .single();

        if (createdFormError) throw createdFormError;

        const createdForm = mapFormRow(createdFormRaw as Record<string, unknown>);
        createdFormId = createdForm.id;

        const sourceFields = ((sourceFieldsRaw ?? []) as Array<Record<string, unknown>>).map(mapFieldRow);
        if (sourceFields.length > 0) {
          const { error: insertFieldError } = await supabase
            .from('form_fields')
            .insert(
              sourceFields.map((field) => ({
                form_id: createdForm.id,
                field_key: field.field_key,
                field_type: field.field_type,
                label: field.label,
                help_text: field.help_text,
                placeholder: field.placeholder,
                static_text: field.static_text,
                options: field.options,
                field_settings: field.field_settings,
                is_required: field.is_required,
                sort_order: field.sort_order,
              })),
            );

          if (insertFieldError) throw insertFieldError;
        }

        return createdForm;
      } catch (error) {
        if (createdFormId) {
          await supabase.from('forms').delete().eq('id', createdFormId);
        }
        throw error;
      }
    },
    onSuccess: async (form) => {
      notifications.show({
        color: 'teal',
        title: 'Kopie erstellt',
        message: 'Formular und Bausteine wurden als neuer Entwurf dupliziert.',
      });
      setSelectedFormId(form.id);
      setIsCreatingForm(false);
      setSlugTouched(true);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['forms'] }),
        qc.invalidateQueries({ queryKey: ['form-fields', form.id] }),
        qc.invalidateQueries({ queryKey: ['form-submissions'] }),
      ]);
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Kopie fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Formular konnte nicht kopiert werden.',
      });
    },
  });

  const saveFieldM = useMutation({
    mutationFn: async (): Promise<FormFieldRow> => {
      if (!selectedFormId) throw new Error('Bitte zuerst ein Formular speichern oder auswählen.');

      const label = fieldEditor.label.trim();
      const staticText = fieldEditor.staticText.trim();
      if (fieldEditor.fieldType === 'section' && !label) {
        throw new Error('Abschnittsüberschriften brauchen ein Label.');
      }
      if (fieldEditor.fieldType === 'text' && !staticText) {
        throw new Error('Textblöcke brauchen einen Inhalt.');
      }
      if (!isStaticField(fieldEditor.fieldType) && !label) {
        throw new Error('Feld-Label fehlt.');
      }

      const fieldKey = (fieldEditor.fieldKey.trim() || fieldKeyFromLabel(label || staticText)).slice(0, 64);
      const sortOrder = Number(fieldEditor.sortOrder);
      const payload = {
        form_id: selectedFormId,
        field_key: fieldKey,
        field_type: fieldEditor.fieldType,
        label,
        help_text: fieldEditor.helpText.trim(),
        placeholder: fieldEditor.placeholder.trim(),
        static_text: staticText,
        options: linesToOptions(fieldEditor.optionsText),
        field_settings: isPersonListField(fieldEditor.fieldType)
          ? { profileSuggestions: fieldEditor.profileSuggestions }
          : {},
        is_required: !isStaticField(fieldEditor.fieldType) && fieldEditor.isRequired,
        sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
      };

      if (fieldEditor.id) {
        const { data, error } = await supabase
          .from('form_fields')
          .update(payload)
          .eq('id', fieldEditor.id)
          .select('*')
          .single();

        if (error) throw error;
        return mapFieldRow(data as Record<string, unknown>);
      }

      const { data, error } = await supabase
        .from('form_fields')
        .insert(payload)
        .select('*')
        .single();

      if (error) throw error;
      return mapFieldRow(data as Record<string, unknown>);
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Gespeichert',
        message: 'Formularfeld gespeichert.',
      });
      setFieldEditor(emptyFieldEditor(nextFieldSortOrder(fieldsQ.data ?? [])));
      await qc.invalidateQueries({ queryKey: ['form-fields', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Feld konnte nicht gespeichert werden',
        message: error instanceof Error ? error.message : 'Bitte Eingaben prüfen.',
      });
    },
  });

  const deleteFieldM = useMutation({
    mutationFn: async (fieldId: string) => {
      const { error } = await supabase.from('form_fields').delete().eq('id', fieldId);
      if (error) throw error;
    },
    onSuccess: async (_, fieldId) => {
      if (fieldEditor.id === fieldId) {
        setFieldEditor(emptyFieldEditor(nextFieldSortOrder(fieldsQ.data ?? [])));
      }
      notifications.show({
        color: 'teal',
        title: 'Gelöscht',
        message: 'Formularfeld entfernt.',
      });
      await qc.invalidateQueries({ queryKey: ['form-fields', selectedFormId] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Löschen fehlgeschlagen',
        message: error instanceof Error ? error.message : 'Feld konnte nicht gelöscht werden.',
      });
    },
  });

  const reorderFieldM = useMutation({
    mutationFn: async (swaps: Array<{ fieldId: string; sortOrder: number }>) => {
      await Promise.all(
        swaps.map(({ fieldId, sortOrder }) =>
          supabase.from('form_fields').update({ sort_order: sortOrder }).eq('id', fieldId),
        ),
      );
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['form-fields', selectedFormId] });
    },
  });

  const moveField = (field: FormFieldRow, direction: 'up' | 'down') => {
    const fields = fieldsQ.data ?? [];
    const index = fields.findIndex((f) => f.id === field.id);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    const target = fields[targetIndex];
    const sortA = target.sort_order === field.sort_order
      ? target.sort_order + (direction === 'up' ? -1 : 1)
      : target.sort_order;
    reorderFieldM.mutate([
      { fieldId: field.id, sortOrder: sortA },
      { fieldId: target.id, sortOrder: field.sort_order },
    ]);
  };

  const fieldTypeOptions = useMemo(
    () => FORM_FIELD_TYPE_OPTIONS.map((entry) => ({ value: entry.value, label: entry.label })),
    [],
  );

  return (
    <Stack gap="md" className="contrast-page">
      <PageHeader
        title="Formulare"
        description="Öffentliche Formulare bauen, veröffentlichen, auswerten und exportieren."
        loading={formsQ.isLoading || fieldsQ.isLoading || submissionsQ.isLoading}
        actions={(
          <Group gap="xs">
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => {
                void qc.invalidateQueries({ queryKey: ['forms'] });
                if (selectedFormId) {
                  void qc.invalidateQueries({ queryKey: ['form-fields', selectedFormId] });
                  void qc.invalidateQueries({ queryKey: ['form-submissions', selectedFormId] });
                }
              }}
            >
              Aktualisieren
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setIsCreatingForm(true);
                setSelectedFormId('');
                setSlugTouched(false);
                setFormEditor(emptyFormEditor());
                setFieldEditor(emptyFieldEditor());
              }}
            >
              Neues Formular
            </Button>
          </Group>
        )}
      />

      {/* ── Formular-Liste + Editor ─────────────────────────────────────────── */}
      <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
        {/* Linke Spalte: Formular-Liste */}
        <Card radius="lg" p="md" className="contrast-surface">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={700}>Formulare</Text>
              <Badge variant="light">{(formsQ.data ?? []).length}</Badge>
            </Group>

            {(formsQ.data ?? []).length === 0 ? (
              <EmptyState
                icon={<IconPlus size={18} />}
                title="Noch keine Formulare"
                description="Lege das erste Formular an und füge anschließend Felder hinzu."
              />
            ) : (
              <ScrollArea h={440}>
                <Stack gap="xs">
                  {(formsQ.data ?? []).map((form) => {
                    const availabilityState = getFormAvailabilityState(form);
                    const isSelected = selectedFormId === form.id && !isCreatingForm;
                    return (
                      <Card
                        key={form.id}
                        radius="md"
                        p="sm"
                        className={isSelected ? 'contrast-surface-soft' : 'contrast-surface'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedFormId(form.id);
                          setIsCreatingForm(false);
                          setSlugTouched(true);
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap" gap="xs">
                          <Box style={{ minWidth: 0, flex: 1 }}>
                            <Text fw={600} size="sm" truncate>{form.title}</Text>
                            <Text size="xs" c="dimmed" truncate>/forms/{form.slug}</Text>
                            <Group gap={4} mt={4}>
                              <Badge color={statusColor(form.status)} size="xs" variant="light">
                                {statusLabel(form.status)}
                              </Badge>
                              <Badge color={availabilityColor(availabilityState)} size="xs" variant="outline">
                                {availabilityLabel(availabilityState)}
                              </Badge>
                              {form.show_on_homepage && (
                                <Badge size="xs" variant="dot" color="blue">In App</Badge>
                              )}
                              {form.notification_sent_at && (
                                <Badge size="xs" variant="light" color="teal">
                                  Push gesendet
                                </Badge>
                              )}
                            </Group>
                            {(form.published_at || form.notification_sent_at) && (
                              <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                                {[
                                  form.published_at ? `Veröffentlicht ${formatDateTime(form.published_at)}` : '',
                                  form.notification_sent_at ? `Push ${formatDateTime(form.notification_sent_at)}` : '',
                                ].filter(Boolean).join(' · ')}
                              </Text>
                            )}
                            {form.short_description.trim().length > 0 && (
                              <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                                {form.short_description}
                              </Text>
                            )}
                          </Box>
                          <Group gap={4} style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            <Tooltip label="Im Browser öffnen" withArrow>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                disabled={!form.public_url}
                                onClick={() => window.open(form.public_url, '_blank', 'noopener,noreferrer')}
                              >
                                <IconExternalLink size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Duplizieren" withArrow>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                loading={duplicateFormM.isPending}
                                onClick={() => duplicateFormM.mutate(form)}
                              >
                                <IconCopy size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Löschen" withArrow>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                color="red"
                                loading={deleteFormM.isPending}
                                onClick={() => {
                                  if (!window.confirm(`Formular "${form.title}" wirklich löschen? Vorhandene Einreichungen werden ebenfalls entfernt.`)) return;
                                  deleteFormM.mutate(form.id);
                                }}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              </ScrollArea>
            )}
          </Stack>
        </Card>

        {/* Rechte Spalte: Formular-Editor mit Tabs */}
        <Card radius="lg" p="md" className="contrast-surface">
          <Stack gap="sm">
            <Text fw={700}>{formEditor.id ? 'Formular bearbeiten' : 'Neues Formular'}</Text>

            <Tabs defaultValue="grundlagen" className="contrast-tabs">
              <Tabs.List grow>
                <Tabs.Tab value="grundlagen">Grundlagen</Tabs.Tab>
                <Tabs.Tab value="verfuegbarkeit">Verfügbarkeit</Tabs.Tab>
                <Tabs.Tab value="homepage">App</Tabs.Tab>
                <Tabs.Tab value="abschluss">Abschluss</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="grundlagen" pt="sm">
                <Stack gap="sm">
                  <TextInput
                    label="Titel"
                    value={formEditor.title}
                    onChange={(event) => {
                      const title = event.currentTarget.value;
                      setFormEditor((prev) => ({
                        ...prev,
                        title,
                        pushBody: prev.pushBodyTouched ? prev.pushBody : clipFormPushText(buildDefaultFormPushBody(title)),
                        slug: slugTouched ? prev.slug : fieldKeyFromLabel(title).replace(/_/g, '-'),
                      }));
                    }}
                  />
                  <TextInput
                    label="URL-Slug"
                    value={formEditor.slug}
                    onChange={(event) => {
                      setSlugTouched(true);
                      setFormEditor((prev) => ({
                        ...prev,
                        slug: event.currentTarget.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]+/g, '-')
                          .replace(/-+/g, '-')
                          .replace(/^-|-$/g, ''),
                      }));
                    }}
                    description={currentPublicUrl || 'Wird automatisch in die öffentliche URL übernommen.'}
                    rightSection={
                      currentPublicUrl ? (
                        <Tooltip label="URL kopieren" withArrow>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => void navigator.clipboard.writeText(currentPublicUrl)}
                          >
                            <IconCopy size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : undefined
                    }
                  />
                  <Textarea
                    label="Kurztext"
                    minRows={2}
                    value={formEditor.shortDescription}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      setFormEditor((prev) => ({ ...prev, shortDescription: nextValue }));
                    }}
                    description="Kurzbeschreibung für Listen und Hinweis in der App."
                  />
                  <Textarea
                    label="Einleitung / öffentlicher Beschreibungstext"
                    minRows={3}
                    value={formEditor.introText}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      setFormEditor((prev) => ({ ...prev, introText: nextValue }));
                    }}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="verfuegbarkeit" pt="sm">
                <Stack gap="sm">
                  <Select
                    label="Status"
                    data={[
                      { value: 'draft', label: 'Entwurf' },
                      { value: 'published', label: 'Veröffentlicht' },
                      { value: 'archived', label: 'Archiviert' },
                    ]}
                    value={formEditor.status}
                    onChange={(value) => {
                      const next = (value || 'draft') as FormStatus;
                      setFormEditor((prev) => ({ ...prev, status: next }));
                    }}
                    allowDeselect={false}
                  />
                  <Group grow>
                    <TextInput
                      label="Aktiv ab"
                      type="datetime-local"
                      value={formEditor.startsAt}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, startsAt: nextValue }));
                      }}
                    />
                    <TextInput
                      label="Aktiv bis"
                      type="datetime-local"
                      value={formEditor.endsAt}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, endsAt: nextValue }));
                      }}
                    />
                  </Group>
                  <Card radius="md" p="sm" className="contrast-surface-soft">
                    <Stack gap={6}>
                      <Group justify="space-between" wrap="wrap">
                        <Text fw={600}>Öffentliche Verfügbarkeit</Text>
                        <Badge color={availabilityColor(editorAvailabilityState)} variant="light">
                          {availabilityLabel(editorAvailabilityState)}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {formEditor.status === 'draft'
                          ? 'Das Formular ist nur als Entwurf sichtbar und öffentlich nicht erreichbar.'
                          : formEditor.status === 'archived'
                            ? 'Das Formular ist archiviert und öffentlich nicht erreichbar.'
                            : editorAvailabilityState === 'live'
                              ? 'Das Formular ist veröffentlicht und aktuell öffentlich aufrufbar.'
                              : editorAvailabilityState === 'scheduled'
                                ? 'Das Formular ist veröffentlicht, wird aber erst mit dem gesetzten Startzeitpunkt öffentlich.'
                                : 'Das Formular ist veröffentlicht, das Aktivitätsfenster ist aber aktuell nicht offen.'}
                      </Text>
                    </Stack>
                  </Card>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="homepage" pt="sm">
                <Stack gap="sm">
                  <Switch
                    checked={formEditor.showOnHomepage}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.checked;
                      setFormEditor((prev) => ({ ...prev, showOnHomepage: nextValue }));
                    }}
                    label="In App darstellen"
                  />
                  <Text size="sm" c="dimmed">
                    App-Titel, Kurztext und Push können hier immer vorbereitet werden. Gesendet wird die Push erst,
                    wenn das Formular beim Speichern tatsächlich live und in der App sichtbar ist.
                  </Text>
                  <Stack gap="sm">
                    <TextInput
                      label="Titel in App"
                      value={formEditor.homepageTitle}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, homepageTitle: nextValue }));
                      }}
                      placeholder="Optional, Fallback ist der Formulartitel"
                    />
                    <Textarea
                      label="Kurztext in App"
                      minRows={2}
                      value={formEditor.homepageText}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, homepageText: nextValue }));
                      }}
                    />
                    <TextInput
                      label="CTA-Label in App"
                      value={formEditor.homepageCtaLabel}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, homepageCtaLabel: nextValue }));
                      }}
                      placeholder="z.B. Jetzt anmelden"
                    />
                    {selectedForm?.notification_sent_at && (
                      <Alert
                        icon={<IconInfoCircle size={16} />}
                        color="blue"
                        variant="light"
                        title="Push-Benachrichtigung bereits gesendet"
                      >
                        Eine Push-Benachrichtigung wurde am {formatDateTime(selectedForm.notification_sent_at)} verschickt. Eine erneute Benachrichtigung ist nur möglich, wenn das Formular archiviert und neu veröffentlicht wird.
                      </Alert>
                    )}
                    <Card radius="md" p="sm" className="contrast-surface-soft">
                      <Stack gap={6}>
                        <Group justify="space-between" wrap="wrap">
                          <Text fw={600}>Push-Vorschau</Text>
                          <Badge color={willPublishLiveFromDraft ? 'yellow' : 'gray'} variant="light">
                            {willPublishLiveFromDraft ? 'Beim Veröffentlichen auswählbar' : 'Nur Vorschau'}
                          </Badge>
                        </Group>
                        <Text size="sm" c="dimmed">
                          {willPublishLiveFromDraft
                            ? 'Beim Speichern wird zuerst die Veröffentlichung bestätigt. Danach entscheidest du separat, ob diese Push gesendet wird.'
                            : 'Diese Push wird nicht automatisch beim Speichern gesendet.'}
                        </Text>
                        <Card radius="sm" p="sm" className="contrast-surface">
                          <Stack gap={4}>
                            <Text size="xs" c="dimmed">Überschrift</Text>
                            <Text fw={700}>{DEFAULT_FORM_PUSH_TITLE}</Text>
                            <Text size="xs" c="dimmed" mt={4}>Text</Text>
                            <Text>{resolvedFormPushBody}</Text>
                          </Stack>
                        </Card>
                      </Stack>
                    </Card>
                    <Textarea
                      label="Push-Text"
                      minRows={3}
                      value={formEditor.pushBody}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({
                          ...prev,
                          pushBody: nextValue,
                          pushBodyTouched: true,
                        }));
                      }}
                      description="Leer lassen oder Standardtext verwenden, wenn keine eigene Nachricht nötig ist."
                    />
                    <Group gap="xs">
                      <Button
                        variant="light"
                        leftSection={<IconSparkles size={16} />}
                        loading={generateFormPushBodyM.isPending}
                        disabled={!formEditor.title.trim()}
                        onClick={() => generateFormPushBodyM.mutate()}
                      >
                        KI-Text generieren
                      </Button>
                      <Button
                        variant="subtle"
                        onClick={() => {
                          setFormEditor((prev) => ({
                            ...prev,
                            pushBody: clipFormPushText(buildDefaultFormPushBody(prev.title)),
                            pushBodyTouched: false,
                          }));
                        }}
                      >
                        Standardtext wiederherstellen
                      </Button>
                    </Group>
                  </Stack>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="abschluss" pt="sm">
                <Stack gap="sm">
                  <Group grow>
                    <TextInput
                      label="Button-Label"
                      value={formEditor.submitButtonLabel}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, submitButtonLabel: nextValue }));
                      }}
                    />
                    <TextInput
                      label="Bestätigungstitel"
                      value={formEditor.confirmationTitle}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFormEditor((prev) => ({ ...prev, confirmationTitle: nextValue }));
                      }}
                    />
                  </Group>
	                  <Textarea
	                    label="Bestätigungstext"
	                    minRows={2}
	                    value={formEditor.confirmationMessage}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
	                      setFormEditor((prev) => ({ ...prev, confirmationMessage: nextValue }));
	                    }}
	                  />
	                  <Card radius="sm" p="sm" className="contrast-surface-soft">
	                    <Switch
	                      label="Antworten nach dem Abschicken bearbeitbar lassen"
	                      description="Angemeldete App-Nutzer können ihre eigene Einreichung ändern, solange das Formular geöffnet ist."
	                      checked={formEditor.allowSubmissionEdit}
	                      onChange={(event) => {
	                        const checked = event.currentTarget.checked;
	                        setFormEditor((prev) => ({ ...prev, allowSubmissionEdit: checked }));
	                      }}
	                    />
	                  </Card>
	                </Stack>
	              </Tabs.Panel>
            </Tabs>

            <Group>
              <Button
                leftSection={<IconCheck size={16} />}
                loading={saveFormM.isPending}
                onClick={() => {
                  if (!willPublishLiveFromDraft) {
                    saveFormM.mutate({ suppressPushOnce: true });
                    return;
                  }

                  const shouldPublish = window.confirm(
                    'Dieses Formular wird jetzt für berechtigte Nutzer sichtbar. Fortfahren?',
                  );
                  if (!shouldPublish) return;

                  const sendPush = window.confirm(
                    'Optional kann jetzt eine Pushmeldung an Nutzer gesendet werden.\n\nOK = Push senden\nAbbrechen = ohne Push veröffentlichen',
                  );

                  saveFormM.mutate({ suppressPushOnce: !sendPush });
                }}
              >
                Speichern
              </Button>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => {
                  setSlugTouched(false);
                  setIsCreatingForm(false);
                  setFieldEditor(emptyFieldEditor());
                  if (selectedForm) {
                    setSelectedFormId(selectedForm.id);
                  } else {
                    setFormEditor(emptyFormEditor());
                  }
                }}
              >
                Zurücksetzen
              </Button>
            </Group>
          </Stack>
        </Card>
      </SimpleGrid>

      {/* ── Formular-Baukasten ─────────────────────────────────────────────── */}
      <Card radius="lg" p="md" className="contrast-surface">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={700}>Formular-Baukasten</Text>
            <Badge variant="light">{(fieldsQ.data ?? []).length} Bausteine</Badge>
          </Group>

          {!selectedFormId ? (
            <Text c="dimmed" size="sm">
              Speichere zuerst ein Formular, bevor du Felder hinzufügen kannst.
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
              {/* Linke Spalte: Baustein-Editor */}
              <Card radius="md" p="sm" className="contrast-surface-soft">
                <Stack gap="sm">
                  <Text fw={600}>{fieldEditor.id ? 'Baustein bearbeiten' : 'Neuer Baustein'}</Text>

                  <Select
                    label="Typ"
                    data={fieldTypeOptions}
                    value={fieldEditor.fieldType}
                    onChange={(value) => {
                      const next = (value || 'text_input') as FieldEditorState['fieldType'];
                      setFieldEditor((prev) => ({
                        ...prev,
                        fieldType: next,
                        isRequired: isStaticField(next) ? false : prev.isRequired,
                        optionsText: isSelectionField(next) ? prev.optionsText : '',
                        placeholder: next === 'section' || next === 'text' ? '' : prev.placeholder,
                        staticText: next === 'text' ? prev.staticText : next === 'section' ? '' : prev.staticText,
                      }));
                    }}
                    allowDeselect={false}
                  />

                  <TextInput
                    label={fieldEditor.fieldType === 'text' ? 'Optionaler Blocktitel' : 'Label'}
                    value={fieldEditor.label}
                    onChange={(event) => {
                      const nextLabel = event.currentTarget.value;
                      setFieldEditor((prev) => ({
                        ...prev,
                        label: nextLabel,
                        fieldKey: prev.fieldKeyTouched
                          ? prev.fieldKey
                          : (nextLabel.trim().length > 0 ? fieldKeyFromLabel(nextLabel) : ''),
                      }));
                    }}
                  />

                  {fieldEditor.fieldType === 'text' ? (
                    <Textarea
                      label="Text / Hinweis"
                      minRows={3}
                      value={fieldEditor.staticText}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFieldEditor((prev) => ({ ...prev, staticText: nextValue }));
                      }}
                    />
                  ) : (
                    <>
                      <Textarea
                        label="Hilfetext"
                        minRows={2}
                        value={fieldEditor.helpText}
                        onChange={(event) => {
                          const nextValue = event.currentTarget.value;
                          setFieldEditor((prev) => ({ ...prev, helpText: nextValue }));
                        }}
                      />
                      <TextInput
                        label="Placeholder"
                        value={fieldEditor.placeholder}
                        disabled={fieldEditor.fieldType === 'section' || fieldEditor.fieldType === 'checkbox' || fieldEditor.fieldType === 'checkbox_group' || fieldEditor.fieldType === 'radio'}
                        onChange={(event) => {
                          const nextValue = event.currentTarget.value;
                          setFieldEditor((prev) => ({ ...prev, placeholder: nextValue }));
                        }}
                      />
                    </>
                  )}

                  {isSelectionField(fieldEditor.fieldType) && (
                    <Textarea
                      label="Optionen (eine pro Zeile)"
                      minRows={3}
                      value={fieldEditor.optionsText}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setFieldEditor((prev) => ({ ...prev, optionsText: nextValue }));
                      }}
                    />
                  )}

                  {isPersonListField(fieldEditor.fieldType) && (
                    <Stack gap="xs">
                      <Text size="sm" c="dimmed">
                        Dieses Feld wird öffentlich als wiederholbare Namensliste angezeigt. Bekannte App-Benutzer können vorgeschlagen werden; freie Namen bleiben möglich.
                      </Text>
                      <Switch
                        checked={fieldEditor.profileSuggestions}
                        onChange={(event) => {
                          const nextValue = event.currentTarget.checked;
                          setFieldEditor((prev) => ({ ...prev, profileSuggestions: nextValue }));
                        }}
                        label="Vorschläge aus App-Benutzern anzeigen"
                      />
                    </Stack>
                  )}

                  {!isStaticField(fieldEditor.fieldType) && (
                    <Switch
                      checked={fieldEditor.isRequired}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.checked;
                        setFieldEditor((prev) => ({ ...prev, isRequired: nextValue }));
                      }}
                      label="Pflichtfeld"
                    />
                  )}

                  <Accordion variant="contained" radius="sm">
                    <Accordion.Item value="advanced">
                      <Accordion.Control>
                        <Text size="xs" c="dimmed">Erweitert</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          <TextInput
                            label="Technischer Key / stabile Feld-ID"
                            value={fieldEditor.fieldKey}
                            onChange={(event) => {
                              const nextValue = event.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_');
                              setFieldEditor((prev) => ({ ...prev, fieldKey: nextValue, fieldKeyTouched: true }));
                            }}
                            placeholder="z.B. probenwochenende_anreise"
                          />
                          <TextInput
                            label="Sortierung"
                            value={fieldEditor.sortOrder}
                            onChange={(event) => {
                              const nextValue = event.currentTarget.value;
                              setFieldEditor((prev) => ({ ...prev, sortOrder: nextValue }));
                            }}
                          />
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>

                  <Group>
                    <Button
                      leftSection={<IconCheck size={16} />}
                      loading={saveFieldM.isPending}
                      onClick={() => saveFieldM.mutate()}
                    >
                      Feld speichern
                    </Button>
                    <Button
                      variant="light"
                      leftSection={<IconRefresh size={16} />}
                      onClick={startNewFieldEditor}
                    >
                      {fieldEditor.id ? 'Neuer Baustein' : 'Feld zurücksetzen'}
                    </Button>
                  </Group>
                </Stack>
              </Card>

              {/* Rechte Spalte: Feldliste */}
              <Card radius="md" p="sm" className="contrast-surface-soft">
                <Stack gap="sm">
                  <Text fw={600}>
                    {(fieldsQ.data ?? []).length === 0 ? 'Noch keine Bausteine' : `${(fieldsQ.data ?? []).length} Bausteine`}
                  </Text>
                  {(fieldsQ.data ?? []).length === 0 ? (
                    <Text c="dimmed" size="sm">Füge links einen neuen Baustein hinzu.</Text>
                  ) : (
                    <Stack gap="xs">
                      {(fieldsQ.data ?? []).map((field, index) => (
                        <Card
                          key={field.id}
                          radius="sm"
                          p="xs"
                          className={fieldEditor.id === field.id ? 'contrast-surface-soft' : 'contrast-surface'}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setFieldEditor({
                              id: field.id,
                              fieldKey: field.field_key,
                              fieldKeyTouched: true,
                              label: field.label,
                              fieldType: field.field_type,
                              helpText: field.help_text,
                              placeholder: field.placeholder,
                              staticText: field.static_text,
                              optionsText: optionsToLines(field.options),
                              profileSuggestions: personListUsesProfileSuggestions(field),
                              isRequired: field.is_required,
                              sortOrder: String(field.sort_order),
                            });
                          }}
                        >
                          <Group justify="space-between" wrap="nowrap" gap="xs">
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Group gap={4} wrap="nowrap">
                                <Text fw={600} size="sm" truncate>
                                  {field.label || field.field_key}
                                </Text>
                                <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>
                                  {FORM_FIELD_TYPE_OPTIONS.find((entry) => entry.value === field.field_type)?.label ?? field.field_type}
                                </Badge>
                                {field.is_required && (
                                  <Badge size="xs" color="red" variant="outline" style={{ flexShrink: 0 }}>
                                    Pflicht
                                  </Badge>
                                )}
                              </Group>
                              {field.help_text && (
                                <Text size="xs" c="dimmed" lineClamp={1}>{field.help_text}</Text>
                              )}
                              {field.static_text && (
                                <Text size="xs" lineClamp={1}>{field.static_text}</Text>
                              )}
                              {field.options.length > 0 && (
                                <Text size="xs" c="dimmed" lineClamp={1}>{field.options.join(' · ')}</Text>
                              )}
                            </Box>
                            <Group gap={2} style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                              <Stack gap={1}>
                                <ActionIcon
                                  size="xs"
                                  variant="subtle"
                                  disabled={index === 0 || reorderFieldM.isPending}
                                  onClick={() => moveField(field, 'up')}
                                >
                                  <IconArrowUp size={10} />
                                </ActionIcon>
                                <ActionIcon
                                  size="xs"
                                  variant="subtle"
                                  disabled={index === (fieldsQ.data ?? []).length - 1 || reorderFieldM.isPending}
                                  onClick={() => moveField(field, 'down')}
                                >
                                  <IconArrowDown size={10} />
                                </ActionIcon>
                              </Stack>
                              <Tooltip label="Löschen" withArrow>
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="red"
                                  loading={deleteFieldM.isPending}
                                  onClick={() => {
                                    if (!window.confirm(`Baustein "${field.label || field.field_key}" wirklich löschen?`)) return;
                                    deleteFieldM.mutate(field.id);
                                  }}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
            </SimpleGrid>
          )}
        </Stack>
      </Card>

      <Card radius="lg" p="md" className="contrast-surface">
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap">
            <Box>
              <Text fw={700}>Einreichungen & Auswertung</Text>
              <Text size="sm" c="dimmed">
                Suche, Detailansicht, Kennzahlen, Gruppierungen und Excel-Export.
              </Text>
            </Box>
            <Group gap="xs">
              <Switch
                checked={showTestSubmissions}
                onChange={(event) => setShowTestSubmissions(event.currentTarget.checked)}
                label="Testdaten anzeigen"
              />
              <TextInput
                leftSection={<IconSearch size={14} />}
                value={submissionSearch}
                onChange={(event) => setSubmissionSearch(event.currentTarget.value)}
                placeholder="Suche in Antworten"
              />
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                disabled={(submissionsQ.data ?? []).length === 0}
                onClick={() => {
                  const rows = buildExportRows(fieldsQ.data ?? [], submissionsQ.data ?? []);
                  const workbook = XLSX.utils.book_new();
                  const worksheet = XLSX.utils.json_to_sheet(rows);
                  XLSX.utils.book_append_sheet(
                    workbook,
                    worksheet,
                    safeSheetName(selectedForm?.title ?? 'Formulare'),
                  );
                  const filename = `${selectedForm?.slug || 'form'}-${new Date().toISOString().slice(0, 10)}.xlsx`;
                  XLSX.writeFile(workbook, filename);
                }}
              >
                Excel Export
              </Button>
            </Group>
          </Group>

          {!selectedFormId ? (
            <Text c="dimmed" size="sm">
              Wähle zuerst ein Formular aus.
            </Text>
          ) : (
            <>
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm">
                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Text size="sm" c="dimmed">
                    Einreichungen gesamt
                  </Text>
                  <Text fw={700} size="xl">
                    {(submissionsQ.data ?? []).length}
                  </Text>
                </Card>
                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Text size="sm" c="dimmed">
                    Letzte Einreichung
                  </Text>
                  <Text fw={700}>
                    {selectedFormId && (submissionsQ.data ?? []).length > 0
                      ? formatDateTime((submissionsQ.data ?? [])[0].submitted_at)
                      : '–'}
                  </Text>
                </Card>
                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Text size="sm" c="dimmed">
                    Aktive Eingabefelder
                  </Text>
                  <Text fw={700} size="xl">
                    {(fieldsQ.data ?? []).filter((field) => !isStaticField(field.field_type)).length}
                  </Text>
                </Card>
              </SimpleGrid>

              {!showTestSubmissions && (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  color="gray"
                  variant="light"
                >
                  Testeinreichungen (z.B. Admin-Vorschauen) sind ausgeblendet. Aktiviere „Testdaten anzeigen", um sie einzubeziehen.
                </Alert>
              )}

              <Tabs
                value={analysisTab === 'rooms' && !hasRoomPlanningField ? 'submissions' : analysisTab}
                onChange={(value) => setAnalysisTab((value ?? 'submissions') as AnalysisTab)}
                keepMounted={false}
                className="contrast-tabs"
              >
                <Tabs.List grow>
                  <Tabs.Tab value="submissions">Einreichungen</Tabs.Tab>
                  <Tabs.Tab value="stats">Auswertung</Tabs.Tab>
                  {hasRoomPlanningField && (
                    <Tabs.Tab value="rooms">Zimmerplanung</Tabs.Tab>
                  )}
                  <Tabs.Tab value="ablaufplan">Ablaufplan</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="submissions" pt="sm">
              <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Stack gap="sm">
                    <Text fw={600}>Einreichungsliste</Text>
                    {filteredSubmissions.length === 0 ? (
                      <Text c="dimmed" size="sm">
                        Keine Einreichungen gefunden.
                      </Text>
                    ) : (
                      <Table className="contrast-table" withRowBorders={false} highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Eingang</Table.Th>
                            <Table.Th>Quelle</Table.Th>
                            <Table.Th>Vorschau</Table.Th>
                            <Table.Th>Antworten</Table.Th>
                            <Table.Th />
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {filteredSubmissions.map((submission) => (
                            <Table.Tr
                              key={submission.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedSubmissionId(submission.id)}
                            >
                              <Table.Td>{formatDateTime(submission.submitted_at)}</Table.Td>
                              <Table.Td>
                                <Group gap={6}>
                                  <Badge variant="light" color={submissionSourceColor(submission.source)}>
                                    {submissionSourceLabel(submission.source)}
                                  </Badge>
                                  {submission.test_submission && (
                                    <Badge variant="light" color="yellow">
                                      Test
                                    </Badge>
                                  )}
                                </Group>
                              </Table.Td>
                              <Table.Td>{buildSubmissionPreview(submission) || '–'}</Table.Td>
                              <Table.Td>{submission.answers.length}</Table.Td>
                              <Table.Td>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  aria-label="Einreichung löschen"
                                  disabled={deleteSubmissionM.isPending}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSubmissionDeleteTarget(submission);
                                  }}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                </Card>

                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Stack gap="sm">
                    <Text fw={600}>Detailansicht</Text>
                    {!selectedSubmission ? (
                      <Text c="dimmed" size="sm">
                        Noch keine Einreichung ausgewählt.
                      </Text>
                    ) : (
                      <>
                        <Group gap="xs">
                          <Badge variant="light">{formatDateTime(selectedSubmission.submitted_at)}</Badge>
                          <Badge variant="light" color={submissionSourceColor(selectedSubmission.source)}>
                            {submissionSourceLabel(selectedSubmission.source)}
                          </Badge>
                          {selectedSubmission.test_submission && (
                            <Badge variant="light" color="yellow">
                              Testeinreichung
                            </Badge>
                          )}
                          {selectedSubmission.source_ip_hash && (
                            <Badge variant="outline" color="gray">
                              IP Hash vorhanden
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" c="dimmed">
                          Submission ID: <Code>{selectedSubmission.id}</Code>
                        </Text>
                        {selectedSubmission.user_agent && (
                          <Text size="sm" c="dimmed">
                            User-Agent: {selectedSubmission.user_agent}
                          </Text>
                        )}
                        <Divider />
                        <Stack gap="xs">
                          {selectedSubmission.answers.map((answer) => (
                            <Card key={`${selectedSubmission.id}-${answer.field_key}`} radius="sm" p="sm" className="contrast-surface">
                              <Text fw={600}>{answer.field_label || answer.field_key}</Text>
                              <Text size="sm" c="dimmed">
                                {formatStoredAnswerValue(answer) || '–'}
                              </Text>
                            </Card>
                          ))}
                        </Stack>
                        <Group justify="flex-end">
                          <Button
                            color="red"
                            variant="light"
                            leftSection={<IconTrash size={16} />}
                            disabled={deleteSubmissionM.isPending}
                            onClick={() => setSubmissionDeleteTarget(selectedSubmission)}
                          >
                            Einreichung löschen
                          </Button>
                        </Group>
                      </>
                    )}
                  </Stack>
                </Card>
              </SimpleGrid>

                </Tabs.Panel>

                <Tabs.Panel value="stats" pt="sm">
              <Stack gap="md">
              <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Stack gap="sm">
                    <Text fw={600}>Zahlfelder: Kennzahlen</Text>
                    {numericStats.length === 0 ? (
                      <Text c="dimmed" size="sm">
                        Für dieses Formular liegen noch keine auswertbaren Zahlfelder vor.
                      </Text>
                    ) : (
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                        {numericStats.map((entry) => (
                          <Card key={entry.field.id} radius="sm" p="sm" className="contrast-surface">
                            <Stack gap={6}>
                              <Text fw={600}>{entry.field.label}</Text>
                              <Text size="sm" c="dimmed">
                                {entry.answeredCount} Antworten
                              </Text>
                              <Badge variant="light">
                                Durchschnitt: {formatMetricNumber(entry.average)}
                              </Badge>
                              <Group gap="xs">
                                <Badge variant="outline" color="gray">
                                  Min: {formatMetricNumber(entry.min)}
                                </Badge>
                                <Badge variant="outline" color="gray">
                                  Max: {formatMetricNumber(entry.max)}
                                </Badge>
                              </Group>
                            </Stack>
                          </Card>
                        ))}
                      </SimpleGrid>
                    )}
                  </Stack>
                </Card>

                <Card radius="md" p="sm" className="contrast-surface-soft">
                  <Stack gap="sm">
                    <Text fw={600}>Auswahl- und Zustimmungsfelder</Text>
                    {selectionStats.length === 0 ? (
                      <Text c="dimmed" size="sm">
                        Für dieses Formular liegen noch keine auswertbaren Auswahlwerte vor.
                      </Text>
                    ) : (
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                        {selectionStats.map((entry) => (
                          <Card key={entry.field.id} radius="sm" p="sm" className="contrast-surface">
                            <Stack gap="xs">
                              <Text fw={600}>{entry.field.label}</Text>
                              <Text size="sm" c="dimmed">
                                {entry.answeredCount} Antworten
                              </Text>
                              <Group gap="xs">
                                {entry.entries.map((item) => (
                                  <Badge key={`${entry.field.id}-${item.label}`} variant="light">
                                    {item.label}: {item.count} ({Math.round(item.share * 100)}%)
                                  </Badge>
                                ))}
                              </Group>
                            </Stack>
                          </Card>
                        ))}
                      </SimpleGrid>
                    )}
                  </Stack>
                </Card>
              </SimpleGrid>

              {personListFieldOptions.length > 0 && (
              <Card radius="md" p="sm" className="contrast-surface-soft">
                <Stack gap="sm">
                  <Text fw={600}>Mehrere Personen / Zimmerwünsche</Text>
                  {personListInsights.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      Für dieses Formular liegen noch keine zusammenhängenden Namenslisten vor.
                    </Text>
                  ) : (
                    <Stack gap="sm">
                      {personListInsights.map((insight) => (
                        <Card key={insight.field.id} radius="sm" p="sm" className="contrast-surface">
                          <Stack gap="sm">
                            <Box>
                              <Text fw={600}>{insight.field.label}</Text>
                              <Text size="sm" c="dimmed">
                                {insight.groups.length} erkannte Gruppe{insight.groups.length === 1 ? '' : 'n'}
                              </Text>
                            </Box>
                            <Stack gap="xs">
                              {insight.groups.map((group, index) => (
                                <Card key={`${insight.field.id}-${index}`} radius="sm" p="sm" className="contrast-surface-soft">
                                  <Stack gap="xs">
                                    <Group gap="xs">
                                      {group.sharedNames.length > 0 ? group.sharedNames.map((name) => (
                                        <Badge key={`${insight.field.id}-${index}-${name}`} variant="light">
                                          {name}
                                        </Badge>
                                      )) : (
                                        <Badge variant="outline" color="gray">
                                          Verbundene Namensliste
                                        </Badge>
                                      )}
                                    </Group>
                                    <Table className="contrast-table" withRowBorders={false}>
                                      <Table.Thead>
                                      <Table.Tr>
                                          <Table.Th>Eingang</Table.Th>
                                          <Table.Th>Quelle</Table.Th>
                                          <Table.Th>Namen</Table.Th>
                                          <Table.Th>Vorschau</Table.Th>
                                          <Table.Th />
                                        </Table.Tr>
                                      </Table.Thead>
                                      <Table.Tbody>
                                        {group.submissions.map((entry) => (
                                          <Table.Tr
                                            key={entry.submission.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setSelectedSubmissionId(entry.submission.id)}
                                          >
                                            <Table.Td>{formatDateTime(entry.submission.submitted_at)}</Table.Td>
                                            <Table.Td>
                                              <Badge
                                                variant="light"
                                                color={submissionSourceColor(entry.submission.source)}
                                              >
                                                {submissionSourceLabel(entry.submission.source)}
                                              </Badge>
                                            </Table.Td>
                                            <Table.Td>{entry.names.join(' | ') || '–'}</Table.Td>
                                            <Table.Td>{buildSubmissionPreview(entry.submission) || '–'}</Table.Td>
                                            <Table.Td>
                                              <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                aria-label="Einreichung löschen"
                                                disabled={deleteSubmissionM.isPending}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  setSubmissionDeleteTarget(entry.submission);
                                                }}
                                              >
                                                <IconTrash size={16} />
                                              </ActionIcon>
                                            </Table.Td>
                                          </Table.Tr>
                                        ))}
                                      </Table.Tbody>
                                    </Table>
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          </Stack>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
              )}

              </Stack>
                </Tabs.Panel>

                {hasRoomPlanningField && (
                <Tabs.Panel value="rooms" pt="sm">
              <Card radius="md" p="sm" className="contrast-surface-soft">
                <Stack gap="md">
                  <Group justify="space-between" wrap="wrap">
	                    <Box>
	                      <Text fw={600}>Zimmerplanung</Text>
	                      <Text size="sm" c="dimmed">
	                        Schlafzimmer anlegen, Personen auf Zimmer verteilen und die Belegung manuell nachbearbeiten.
	                      </Text>
	                    </Box>
                    <Group gap="xs">
                      <Button
                        variant="light"
                        leftSection={<IconDownload size={16} />}
                        disabled={roomPlanExportRows.length === 0}
                        onClick={() => {
                          const workbook = XLSX.utils.book_new();
                          const worksheet = XLSX.utils.json_to_sheet(roomPlanExportRows);
                          XLSX.utils.book_append_sheet(workbook, worksheet, 'Zimmerplan');
                          if (roomPlanningConflictExportRows.length > 0) {
                            const conflictSheet = XLSX.utils.json_to_sheet(roomPlanningConflictExportRows);
                            XLSX.utils.book_append_sheet(workbook, conflictSheet, 'Konflikte');
                          }
                          const filename = `${selectedForm?.slug || 'form'}-zimmerplan-${new Date().toISOString().slice(0, 10)}.xlsx`;
                          XLSX.writeFile(workbook, filename);
                        }}
                      >
                        Zimmerplan Export
                      </Button>
                      <Tooltip
                        label={proposalDisabledReason || 'Zimmerbelegung automatisch vorschlagen'}
                        disabled={!proposalDisabledReason}
                      >
                        <Box>
                          <Button
                            leftSection={<IconSparkles size={16} />}
                            loading={saveProposalM.isPending}
                            disabled={Boolean(proposalDisabledReason)}
                            onClick={() => saveProposalM.mutate()}
                          >
                            Vorschlag berechnen
                          </Button>
                        </Box>
                      </Tooltip>
                    </Group>
                  </Group>

                  {personListFieldOptions.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      Lege zuerst ein Feld vom Typ „Mehrere Personen / Zimmerwunsch“ an.
                    </Text>
                  ) : (
                    <>
                      <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="sm">
                        <Card radius="sm" p="sm" className="contrast-surface">
                          <Stack gap="sm">
                            <Text fw={600}>Einstellungen</Text>
                            <Switch
                              checked={roomSettingsLocal?.enabled ?? roomConfigQ.data?.enabled ?? false}
                              label="Zimmerplanung aktiv"
                              onChange={(event) => {
                                setRoomSettingsLocal((prev) => ({
                                  enabled: event.currentTarget.checked,
                                  suggestionsEnabled: prev?.suggestionsEnabled ?? roomConfigQ.data?.suggestions_enabled ?? true,
                                  personListFieldId: prev?.personListFieldId ?? selectedPersonListFieldId,
                                }));
                              }}
                            />
                            <Switch
                              checked={roomSettingsLocal?.suggestionsEnabled ?? roomConfigQ.data?.suggestions_enabled ?? true}
                              label="App-Übernahmevorschläge"
                              onChange={(event) => {
                                setRoomSettingsLocal((prev) => ({
                                  enabled: prev?.enabled ?? roomConfigQ.data?.enabled ?? false,
                                  suggestionsEnabled: event.currentTarget.checked,
                                  personListFieldId: prev?.personListFieldId ?? selectedPersonListFieldId,
                                }));
                              }}
                            />
                            <Select
                              label="Wunschpersonen-Feld"
                              data={personListFieldOptions}
                              value={roomSettingsLocal?.personListFieldId ?? selectedPersonListFieldId}
                              allowDeselect={false}
                              onChange={(value) => {
                                setRoomSettingsLocal((prev) => ({
                                  enabled: prev?.enabled ?? roomConfigQ.data?.enabled ?? false,
                                  suggestionsEnabled: prev?.suggestionsEnabled ?? roomConfigQ.data?.suggestions_enabled ?? true,
                                  personListFieldId: value ?? null,
                                }));
                              }}
                            />
                            <Button
                              size="xs"
                              leftSection={<IconCheck size={14} />}
                              loading={saveRoomConfigM.isPending}
                              onClick={() => {
                                const s = roomSettingsLocal;
                                if (!s) return;
                                saveRoomConfigM.mutate({
                                  enabled: s.enabled,
                                  personListFieldId: s.personListFieldId,
                                  suggestionsEnabled: s.suggestionsEnabled,
                                });
                              }}
                            >
                              Einstellungen speichern
                            </Button>
                          </Stack>
                        </Card>

                        <Card radius="sm" p="sm" className="contrast-surface">
                          <Stack gap="sm">
                            <Box>
                              <Text fw={600}>Zimmer nach Anzahl</Text>
                              <Text size="sm" c="dimmed">
                                Erzeugt bearbeitbare Zimmer aus Anzahl und Betten.
                              </Text>
                            </Box>
                            <Group grow>
                              <TextInput
                                label="Anzahl Zimmer"
                                type="number"
                                min={1}
                                value={roomContingentEditor.roomCount}
                                onChange={(event) => setRoomContingentEditor((prev) => ({ ...prev, roomCount: event.currentTarget.value }))}
                              />
                              <TextInput
                                label="Betten pro Zimmer"
                                type="number"
                                min={1}
                                value={roomContingentEditor.capacity}
                                onChange={(event) => setRoomContingentEditor((prev) => ({ ...prev, capacity: event.currentTarget.value }))}
                              />
                            </Group>
                            <TextInput
                              label="Zimmername"
                              value={roomContingentEditor.labelPrefix}
                              placeholder={`${bedroomRoomPrefix(Number(roomContingentEditor.capacity) || 4)} 1`}
                              onChange={(event) => setRoomContingentEditor((prev) => ({ ...prev, labelPrefix: event.currentTarget.value }))}
                            />
                            <Group gap="xs">
                              <Button
                                leftSection={<IconCheck size={16} />}
                                loading={saveRoomContingentM.isPending}
                                onClick={() => saveRoomContingentM.mutate()}
                              >
                                Anzahl speichern
                              </Button>
                              <Button variant="light" onClick={startNewRoomContingentEditor}>
                                Neu
                              </Button>
                            </Group>
                          </Stack>
                        </Card>

                        <Card radius="sm" p="sm" className="contrast-surface">
                          <Stack gap="sm">
                            <Box>
                              <Text fw={600}>Person manuell zuweisen</Text>
                              <Text size="sm" c="dimmed">Aus Einreichungen wählen oder Namen frei eingeben.</Text>
                            </Box>
                            {participantSelectOptions.length > 0 && (
                              <Select
                                label="Aus Einreichungen"
                                placeholder="Person suchen…"
                                data={participantSelectOptions}
                                searchable
                                clearable
                                value={manualRoomParticipant.participantId}
                                onChange={(value) => {
                                  const participant = value ? participantBySubmissionId.get(value) : null;
                                  setManualRoomParticipant((prev) => ({
                                    ...prev,
                                    participantId: value,
                                    name: participant?.displayName ?? '',
                                  }));
                                }}
                              />
                            )}
                            <TextInput
                              label="Oder Name eingeben"
                              placeholder="Externer Name…"
                              value={manualRoomParticipant.name}
                              onChange={(event) => setManualRoomParticipant((prev) => ({
                                ...prev,
                                participantId: null,
                                name: event.currentTarget.value,
                              }))}
                            />
                            <Select
                              label="Zimmer"
                              data={roomOptions}
                              value={manualRoomParticipant.roomId}
                              onChange={(value) => setManualRoomParticipant((prev) => ({ ...prev, roomId: value ?? '' }))}
                            />
                            <Button
                              leftSection={<IconPlus size={16} />}
                              loading={addManualAssignmentM.isPending}
                              disabled={!manualRoomParticipant.name.trim()}
                              onClick={() => addManualAssignmentM.mutate()}
                            >
                              Person hinzufügen
                            </Button>
                          </Stack>
                        </Card>
                      </SimpleGrid>

                      {(() => {
                        const hasContingents = (roomContingentsQ.data ?? []).length > 0;
                        return (
                        <SimpleGrid cols={hasContingents ? { base: 1, xl: 2 } : 1} spacing="sm">
                        {hasContingents && (
                        <Card radius="sm" p="sm" className="contrast-surface">
                          <Stack gap="sm">
                            <Group justify="space-between" wrap="wrap">
                              <Box>
                                <Text fw={600}>Gespeicherte Zimmermengen</Text>
                                <Text size="sm" c="dimmed">
                                  Aus diesen Angaben können konkrete Zimmer erzeugt werden.
                                </Text>
                              </Box>
                              <Button
                                variant="light"
                                size="xs"
                                leftSection={<IconPlus size={14} />}
                                loading={generateRoomsM.isPending}
                                disabled={!hasMissingRooms}
                                onClick={() => generateRoomsM.mutate()}
                              >
                                Zimmer erzeugen
                              </Button>
                            </Group>
                            <Stack gap="xs">
                              {(roomContingentsQ.data ?? []).map((contingent) => (
                                <Card key={contingent.id} radius="sm" p="sm" className="contrast-surface-soft">
                                  <Group justify="space-between" wrap="wrap">
                                    <Box>
                                      <Text fw={600}>
                                        {contingent.room_count}x {contingent.capacity} Betten
                                      </Text>
                                      <Text size="sm" c="dimmed">
                                        {bedroomRoomPrefix(contingent.capacity, contingent.label_prefix)}
                                      </Text>
                                    </Box>
                                    <Group gap="xs">
                                      <Button
                                        size="xs"
                                        variant="light"
                                        onClick={() => setRoomContingentEditor({
                                          id: contingent.id,
                                          roomCount: String(contingent.room_count),
                                          capacity: String(contingent.capacity),
                                          labelPrefix: contingent.label_prefix,
                                          sortOrder: String(contingent.sort_order),
                                        })}
                                      >
                                        Bearbeiten
                                      </Button>
                                      <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        aria-label="Zimmermenge löschen"
                                        onClick={() => {
                                          if (!window.confirm(`Zimmermenge „${contingent.room_count}x ${contingent.capacity} Betten" wirklich löschen?`)) return;
                                          deleteRoomContingentM.mutate(contingent.id);
                                        }}
                                      >
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Group>
                                  </Group>
                                </Card>
                              ))}
                            </Stack>
                          </Stack>
                        </Card>
                        )}

                        <Card radius="sm" p="sm" className="contrast-surface">
                          <Stack gap="sm">
                            <Box>
                              <Text fw={600}>Zimmerliste</Text>
                              <Text size="sm" c="dimmed">
                                Alle Zimmer in dieser Liste werden als Schlafzimmer für die Belegung genutzt.
                              </Text>
                            </Box>
                            <Group gap="xs" align="flex-end" wrap="wrap">
                                  <TextInput
                                    size="xs"
                                    label="Zimmer"
                                    placeholder="z.B. 1.03"
                                    style={{ width: 90 }}
                                    value={roomEditor.name}
                                    onChange={(event) => setRoomEditor((prev) => ({ ...prev, name: event.currentTarget.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && roomEditor.name.trim()) createRoomM.mutate(); }}
                                  />
                                  <TextInput
                                    size="xs"
                                    label="Betten"
                                    type="number"
                                    min={1}
                                    style={{ width: 65 }}
                                    value={roomEditor.capacity}
                                    onChange={(event) => setRoomEditor((prev) => ({ ...prev, capacity: event.currentTarget.value }))}
                                  />
                                  <TextInput
                                    size="xs"
                                    label="Notiz"
                                    placeholder="optional"
                                    style={{ width: 110 }}
                                    value={roomEditor.notes}
                                    onChange={(event) => setRoomEditor((prev) => ({ ...prev, notes: event.currentTarget.value }))}
                                  />
                                  <ActionIcon
                                    size="md"
                                    variant="filled"
                                    loading={createRoomM.isPending}
                                    disabled={!roomEditor.name.trim()}
                                    title="Zimmer hinzufügen"
                                    onClick={() => createRoomM.mutate()}
                                    style={{ marginBottom: 1 }}
                                  >
                                    <IconPlus size={14} />
                                  </ActionIcon>
                                  <ActionIcon
                                    size="md"
                                    variant="light"
                                    color="indigo"
                                    title="JSON importieren"
                                    style={{ marginBottom: 1 }}
                                    onClick={() => {
                                      navigator.clipboard.readText().then((text) => {
                                        setJsonImportText(text);
                                        setJsonImportOpen(true);
                                      }).catch(() => setJsonImportOpen(true));
                                    }}
                                  >
                                    <IconClipboard size={14} />
                                  </ActionIcon>
                                </Group>
                            {bedroomRooms.length === 0 ? (
                              <Text size="sm" c="dimmed">
                                Lege Zimmer direkt an oder erzeuge sie aus Anzahl und Betten.
                              </Text>
                            ) : (
                              <SimpleGrid cols={hasContingents ? 1 : { base: 1, md: 2 }} spacing={4}>
                                {bedroomRooms.map((room) => {
                                  const count = (assignmentsByRoom.get(room.id) ?? []).length;
                                  const isFull = count >= room.capacity;
                                  const warnings = roomWarningsByRoomName.get(room.name) ?? [];
                                  return (
                                    <Group
                                      key={`${room.id}-${room.updated_at}`}
                                      gap="xs"
                                      align="center"
                                      wrap="nowrap"
                                      style={{
                                        padding: '4px 6px',
                                        borderRadius: 6,
                                        borderLeft: room.is_locked ? '3px solid var(--mantine-color-orange-5)' : '3px solid transparent',
                                        background: room.is_locked ? 'color-mix(in srgb, var(--mantine-color-orange-9) 10%, transparent)' : undefined,
                                      }}
                                    >
                                      <TextInput
                                        size="xs"
                                        placeholder="Name"
                                        style={{ width: 80 }}
                                        defaultValue={room.name}
                                        onBlur={(event) => {
                                          const nextName = event.currentTarget.value.trim();
                                          if (!nextName || nextName === room.name) return;
                                          updateRoomM.mutate({ id: room.id, name: nextName, capacity: room.capacity, notes: room.notes });
                                        }}
                                      />
                                      <TextInput
                                        size="xs"
                                        type="number"
                                        placeholder="Betten"
                                        style={{ width: 58 }}
                                        defaultValue={String(room.capacity)}
                                        onBlur={(event) => {
                                          const nextCapacity = Number(event.currentTarget.value);
                                          if (!Number.isFinite(nextCapacity) || nextCapacity < 1 || nextCapacity === room.capacity) return;
                                          updateRoomM.mutate({ id: room.id, name: room.name, capacity: Math.round(nextCapacity), notes: room.notes });
                                        }}
                                      />
                                      <TextInput
                                        size="xs"
                                        placeholder="Notiz"
                                        style={{ flex: 1, minWidth: 60 }}
                                        defaultValue={room.notes}
                                        onBlur={(event) => {
                                          const nextNotes = event.currentTarget.value.trim();
                                          if (nextNotes === room.notes) return;
                                          updateRoomM.mutate({ id: room.id, name: room.name, capacity: room.capacity, notes: nextNotes });
                                        }}
                                      />
                                      <Badge
                                        size="xs"
                                        color={room.is_locked ? 'orange' : warnings.length > 0 ? 'red' : isFull ? 'teal' : 'gray'}
                                        variant="light"
                                        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                      >
                                        {room.is_locked ? `Fix·${count}/${room.capacity}` : `${count}/${room.capacity}`}
                                      </Badge>
                                      <Switch
                                        size="xs"
                                        color="orange"
                                        checked={room.is_locked}
                                        title={room.is_locked ? 'Entsperren' : 'Fixieren'}
                                        onChange={(event) =>
                                          updateRoomM.mutate({ id: room.id, name: room.name, capacity: room.capacity, notes: room.notes, is_locked: event.currentTarget.checked })
                                        }
                                      />
                                      <ActionIcon variant="subtle" color="red" size="xs" aria-label="Zimmer löschen" onClick={() => deleteRoomM.mutate(room.id)}>
                                        <IconTrash size={12} />
                                      </ActionIcon>
                                    </Group>
                                  );
                                })}
                              </SimpleGrid>
                            )}
                          </Stack>
                        </Card>
                      </SimpleGrid>
                        );
                      })()}

                      <Card radius="sm" p="sm" className="contrast-surface">
                        <Stack gap="sm">
                          <Group justify="space-between" wrap="wrap">
                            <Box>
                              <Text fw={600}>Belegung</Text>
                              <Text size="sm" c="dimmed">
                              Vorschlag speichern und anschließend Personen bei Bedarf in andere Zimmer verschieben.
                            </Text>
                          </Box>
                            <Group gap="xs">
                              <Badge
                                variant="light"
                                color={
                                  assignmentProgress.total > 0 && assignmentProgress.assigned >= assignmentProgress.total
                                    ? 'teal'
                                    : assignmentProgress.assigned > 0
                                      ? 'yellow'
                                      : 'gray'
                                }
                              >
                                {assignmentProgress.assigned} / {assignmentProgress.total} eingeplant
                                {assignmentProgress.appProfiles > 0 ? ` (${assignmentProgress.appProfiles} App-Profile)` : ''}
                                {assignmentProgress.external > 0 ? ` (${assignmentProgress.external} extern)` : ''}
                              </Badge>
                              {criticalConflicts.length > 0 && (
                                <Badge color="red" variant="light">
                                  {criticalConflicts.length} Fehler
                                </Badge>
                              )}
                              {warningConflicts.length > 0 && (
                                <Badge color="yellow" variant="light">
                                  {warningConflicts.length} Warnungen
                                </Badge>
                              )}
                              {criticalConflicts.length === 0 && warningConflicts.length === 0 && (
                                <Badge color="teal" variant="light">Keine Konflikte</Badge>
                              )}
                            </Group>
                          </Group>
	                          {(criticalConflicts.length > 0 || warningConflicts.length > 0 || noWishCount > 0 || unresolvedWishNames.length > 0) && (
	                            <Card radius="sm" p="sm" className="contrast-surface-soft">
	                              <Stack gap="sm">
	                                {criticalConflicts.length > 0 && (
                                  <Stack gap="xs">
                                    <Group gap="xs">
                                      <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
                                      <Text fw={700} size="sm" c="red">
                                        {criticalConflicts.length} kritische{criticalConflicts.length === 1 ? 'r Fehler' : ' Fehler'} – bitte vor dem Export beheben
                                      </Text>
                                    </Group>
                                    {criticalConflicts.slice(0, 8).map((conflict, index) => (
                                      <Group key={`crit-${conflict.type}-${index}`} gap="xs" align="flex-start">
                                        <Badge color="red" variant="light">
                                          {roomPlanningConflictLabel(conflict.type)}
                                        </Badge>
                                        <Box style={{ flex: 1 }}>
                                          <Text size="sm" fw={600}>{conflict.label}</Text>
                                          <Text size="sm" c="dimmed">{conflict.detail}</Text>
                                        </Box>
                                      </Group>
                                    ))}
                                    {criticalConflicts.length > 8 && (
                                      <Text size="sm" c="dimmed">
                                        {criticalConflicts.length - 8} weitere Fehler im Export sichtbar.
                                      </Text>
	                                    )}
	                                  </Stack>
	                                )}
	                                {unresolvedWishNames.length > 0 && (
	                                  <Stack gap="xs">
	                                    <Box>
	                                      <Text fw={600} size="sm">Offene Namen zuordnen</Text>
	                                      <Text size="sm" c="dimmed">
	                                        Wähle ein App-Profil aus. Die bestehenden Antworten werden dadurch strukturiert mit user_id gespeichert.
	                                      </Text>
	                                    </Box>
	                                    {unresolvedWishNames.map((entry) => (
	                                      <Group key={normalizePersonMatchToken(entry.rawName)} align="end" wrap="wrap">
	                                        <Box style={{ minWidth: 220, flex: 1 }}>
	                                          <Text size="sm" fw={600}>{entry.rawName}</Text>
	                                          <Text size="xs" c="dimmed">
	                                            {entry.count}x genannt von {entry.requestedBy.slice(0, 3).join(', ')}
	                                            {entry.requestedBy.length > 3 ? ` +${entry.requestedBy.length - 3}` : ''}
	                                          </Text>
	                                        </Box>
	                                        <Select
	                                          searchable
	                                          clearable
	                                          placeholder="Profil auswählen"
	                                          data={profileSelectOptions}
	                                          disabled={roomProfileSuggestionsQ.isLoading || profileSelectOptions.length === 0}
	                                          style={{ minWidth: 260, flex: 1 }}
	                                          onChange={(userId) => {
	                                            if (!userId) return;
	                                            matchRoomWishNameM.mutate({ rawName: entry.rawName, userId });
	                                          }}
	                                        />
	                                      </Group>
	                                    ))}
	                                  </Stack>
	                                )}
	                                {warningConflicts.length > 0 && (
	                                  <Stack gap="xs">
	                                    <Text fw={600} size="sm" c="yellow.7">
                                      {warningConflicts.length} Warnung{warningConflicts.length === 1 ? '' : 'en'}
                                    </Text>
                                    {warningConflicts.slice(0, 6).map((conflict, index) => (
                                      <Group key={`warn-${conflict.type}-${index}`} gap="xs" align="flex-start">
                                        <Badge color="yellow" variant="light">
                                          {roomPlanningConflictLabel(conflict.type)}
                                        </Badge>
                                        <Box style={{ flex: 1 }}>
                                          <Text size="sm" fw={600}>{conflict.label}</Text>
                                          <Text size="sm" c="dimmed">{conflict.detail}</Text>
                                        </Box>
                                      </Group>
                                    ))}
                                    {warningConflicts.length > 6 && (
                                      <Text size="sm" c="dimmed">
                                        {warningConflicts.length - 6} weitere Warnungen im Export sichtbar.
                                      </Text>
                                    )}
                                  </Stack>
                                )}
                                {noWishCount > 0 && (
                                  <Group gap="xs">
                                    <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" />
                                    <Text size="sm" c="dimmed">
                                      {noWishCount} Person{noWishCount === 1 ? '' : 'en'} {noWishCount === 1 ? 'hat' : 'haben'} keine Zimmerwünsche angegeben – {noWishCount === 1 ? 'sie wird' : 'sie werden'} automatisch zugeteilt.
                                    </Text>
                                  </Group>
                                )}
	                              </Stack>
	                            </Card>
	                          )}
	                          {roomWishGroupSummaries.length > 0 && (
	                            <Card radius="sm" p="sm" className="contrast-surface-soft">
	                              <Stack gap="sm">
	                                <Group justify="space-between" wrap="wrap">
	                                  <Box>
	                                    <Text fw={600}>Wunschgruppen</Text>
	                                    <Text size="sm" c="dimmed">
	                                      Nur gegenseitige Wünsche bilden eine Gruppe. Einseitige Wünsche erscheinen als Info, nicht als Gruppen-Mitglied.
	                                    </Text>
	                                  </Box>
	                                  <Badge variant="light">{roomWishGroupSummaries.filter((s) => !s.isSingleGroup && s.mutualWishes.length > 0).length} Gruppe{roomWishGroupSummaries.filter((s) => !s.isSingleGroup && s.mutualWishes.length > 0).length === 1 ? '' : 'n'}</Badge>
	                                </Group>
	                                <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xs">
	                                  {roomWishGroupSummaries.slice(0, 20).map((summary, index) => {
	                                    const groupKey = summary.participantIds.join('|') || summary.names.join('|');
	                                    const isMutualGroup = !summary.isSingleGroup && summary.mutualWishes.length > 0;
	                                    const isOneWayOnly = !summary.isSingleGroup && summary.mutualWishes.length === 0;
	                                    const maxPairs = (summary.names.length * (summary.names.length - 1)) / 2;
	                                    const isCompleteGroup = isMutualGroup && summary.mutualWishes.length === maxPairs && summary.oneWayWishes.length === 0;
	                                    const groupAssignments = summary.participantIds
	                                      .map((participantId) => assignmentsByParticipantId.get(participantId)?.[0])
	                                      .filter(Boolean) as FormRoomAssignmentRow[];
	                                    const assignedRoomIds = [...new Set(groupAssignments.map((a) => a.room_id).filter(Boolean))];
	                                    const allInSameRoom = assignedRoomIds.length === 1 && groupAssignments.length === summary.names.length;
	                                    const partiallyAssigned = assignedRoomIds.length > 0 && !allInSameRoom;
	                                    const currentRoomName = allInSameRoom ? (roomById.get(assignedRoomIds[0]!)?.name ?? null) : null;
	                                    const selectedRoom = groupRoomSelections[groupKey] ?? '';
	                                    const accentColor = isCompleteGroup ? 'green' : isMutualGroup ? 'teal' : isOneWayOnly ? 'yellow' : 'gray';
	                                    const borderColor = allInSameRoom
	                                      ? 'var(--mantine-color-teal-5)'
	                                      : isCompleteGroup
	                                        ? 'var(--mantine-color-green-5)'
	                                        : isMutualGroup
	                                          ? 'var(--mantine-color-teal-5)'
	                                          : isOneWayOnly
	                                            ? 'var(--mantine-color-yellow-5)'
	                                            : undefined;
	                                    return (
	                                    <Card
	                                      key={`${groupKey}-${index}`}
	                                      radius="sm"
	                                      p="md"
	                                      className="contrast-surface"
	                                      style={{
	                                        borderLeft: `3px solid ${borderColor ?? 'transparent'}`,
	                                        background: allInSameRoom
	                                          ? 'color-mix(in srgb, var(--mantine-color-teal-9) 20%, var(--mantine-color-dark-7))'
	                                          : isCompleteGroup
	                                            ? 'color-mix(in srgb, var(--mantine-color-green-9) 30%, var(--mantine-color-dark-7))'
	                                            : undefined,
	                                      }}
	                                    >
	                                      <Stack gap="sm">
	                                        {/* Complete group banner */}
	                                        {isCompleteGroup && (
	                                          <Group gap={6} align="center">
	                                            <IconCircleCheck size={15} color="var(--mantine-color-green-4)" />
	                                            <Text size="xs" fw={700} c="green.4" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
	                                              Alle haben sich gegenseitig gewählt
	                                            </Text>
	                                          </Group>
	                                        )}
	                                        {/* Name badges + status badges */}
	                                        <Group justify="space-between" gap={6} wrap="wrap">
	                                          <Group gap={5} style={{ flex: 1 }} wrap="wrap">
	                                            {summary.names.map((name, nameIndex) => (
	                                              <Badge
	                                                key={`${summary.participantIds[nameIndex] ?? name}-${name}`}
	                                                variant="filled"
	                                                color={accentColor}
	                                                size="md"
	                                                style={{ fontWeight: 600 }}
	                                              >
	                                                {name}
	                                              </Badge>
	                                            ))}
	                                          </Group>
	                                          <Group gap={4} wrap="wrap">
	                                            {currentRoomName && (
	                                              <Badge variant="light" color="indigo" size="sm">{currentRoomName}</Badge>
	                                            )}
	                                            {partiallyAssigned && (
	                                              <Badge variant="light" color="orange" size="sm">Teilweise zugewiesen</Badge>
	                                            )}
	                                            <Badge variant="light" color={accentColor} size="sm">
	                                              {summary.isSingleGroup
	                                                ? 'Kein Treffer'
	                                                : isCompleteGroup
	                                                  ? `${summary.names.length} Personen · Komplett`
	                                                  : isMutualGroup
	                                                    ? `${summary.names.length} Personen`
	                                                    : 'Einseitig'}
	                                            </Badge>
	                                          </Group>
	                                        </Group>
	                                        {/* Sections with labels + colored pills */}
	                                        {summary.mutualWishes.length > 0 && (
	                                          <Stack gap={4}>
	                                            <Group gap={6} align="center">
	                                              <Text size="xs" fw={700} c={isCompleteGroup ? 'green.4' : 'teal.4'} tt="uppercase" style={{ letterSpacing: '0.05em' }}>
	                                                Gegenseitig
	                                              </Text>
	                                              <Box style={{ flex: 1, height: 1, background: isCompleteGroup ? 'var(--mantine-color-green-8)' : 'var(--mantine-color-teal-8)' }} />
	                                            </Group>
	                                            <Group gap={5} wrap="wrap">
	                                              {summary.mutualWishes.map((pair) => (
	                                                <Badge key={pair} variant="light" color={isCompleteGroup ? 'green' : 'teal'} size="sm">{pair}</Badge>
	                                              ))}
	                                            </Group>
	                                          </Stack>
	                                        )}
	                                        {summary.oneWayWishes.length > 0 && (
	                                          <Stack gap={4}>
	                                            <Group gap={6} align="center">
	                                              <Text size="xs" fw={700} c="yellow.5" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
	                                                Einseitig
	                                              </Text>
	                                              <Box style={{ flex: 1, height: 1, background: 'var(--mantine-color-yellow-8)' }} />
	                                            </Group>
	                                            <Group gap={5} wrap="wrap">
	                                              {summary.oneWayWishes.map((wish) => (
	                                                <Badge key={wish} variant="light" color="yellow" size="sm">{wish}</Badge>
	                                              ))}
	                                            </Group>
	                                          </Stack>
	                                        )}
	                                        {summary.unresolvedWishes.length > 0 && (() => {
	                                          // Deduplicate by target name (the part after ' → ').
	                                          // Each entry is '<requester> → <target>' — we resolve per unique target.
	                                          const targetMap = new Map<string, string[]>();
	                                          for (const wish of summary.unresolvedWishes) {
	                                            const parts = wish.split(' → ');
	                                            const target = parts.slice(1).join(' → ').trim() || wish;
	                                            const requester = parts[0]?.trim() ?? '';
	                                            const list = targetMap.get(target) ?? [];
	                                            if (requester) list.push(requester);
	                                            targetMap.set(target, list);
	                                          }
	                                          const uniqueTargets = Array.from(targetMap.entries());
	                                          return (
	                                            <Stack gap={4}>
	                                              <Group gap={6} align="center">
	                                                <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
	                                                  Noch nicht angemeldet
	                                                </Text>
	                                                <Box style={{ flex: 1, height: 1, background: 'var(--mantine-color-dark-4)' }} />
	                                              </Group>
	                                              <Group gap={5} wrap="wrap">
	                                                {uniqueTargets.map(([target, requesters]) => (
	                                                  <Tooltip
	                                                    key={target}
	                                                    label={`Gewünscht von: ${requesters.join(', ')}`}
	                                                    withArrow
	                                                    disabled={requesters.length === 0}
	                                                  >
	                                                    <Badge
	                                                      variant={resolvingWish?.groupKey === groupKey && resolvingWish?.name === target ? 'filled' : 'light'}
	                                                      color={resolvingWish?.groupKey === groupKey && resolvingWish?.name === target ? 'blue' : 'gray'}
	                                                      size="sm"
	                                                      style={{ cursor: 'pointer' }}
	                                                      onClick={() => setResolvingWish((prev) =>
	                                                        prev?.groupKey === groupKey && prev?.name === target ? null : { name: target, groupKey }
	                                                      )}
	                                                    >
	                                                      {target}
	                                                    </Badge>
	                                                  </Tooltip>
	                                                ))}
	                                              </Group>
	                                              {resolvingWish?.groupKey === groupKey && resolvingWish?.name && targetMap.has(resolvingWish.name) && (
	                                                <Stack gap={4} style={{ background: 'color-mix(in srgb, var(--mantine-color-blue-9) 20%, var(--mantine-color-dark-7))', border: '1px solid var(--mantine-color-blue-8)', borderRadius: 'var(--mantine-radius-sm)', padding: '6px 8px' }}>
	                                                  <Text size="xs" c="blue.3" fw={600}>{resolvingWish.name} — App-Profil zuordnen</Text>
	                                                  <Text size="xs" c="dimmed">
	                                                    Gewünscht von {(targetMap.get(resolvingWish.name) ?? []).join(', ')}. Wähle das passende Profil damit die Zimmerbelegung in der App angezeigt wird.
	                                                  </Text>
	                                                  <Group gap={4}>
	                                                    <Select
	                                                      size="xs"
	                                                      placeholder="App-Profil suchen…"
	                                                      searchable
	                                                      clearable
	                                                      autoFocus
	                                                      style={{ flex: 1, minWidth: 200 }}
	                                                      data={profileSelectOptions}
	                                                      disabled={roomProfileSuggestionsQ.isLoading || profileSelectOptions.length === 0}
	                                                      onChange={(userId) => {
	                                                        if (!userId) return;
	                                                        matchRoomWishNameM.mutate({ rawName: resolvingWish.name, userId });
	                                                        setResolvingWish(null);
	                                                      }}
	                                                    />
	                                                    <ActionIcon size="xs" variant="subtle" color="gray" title="Schließen" onClick={() => setResolvingWish(null)}>
	                                                      <IconCheck size={12} />
	                                                    </ActionIcon>
	                                                  </Group>
	                                                </Stack>
	                                              )}
	                                            </Stack>
	                                          );
	                                        })()}
	                                        {summary.isSingleGroup && summary.unresolvedWishes.length === 0 && (
	                                          <Text size="xs" c="dimmed">Keine gegenseitigen Zimmerwünsche.</Text>
	                                        )}
	                                        {/* Room assignment row – shown for all groups */}
	                                        <Group gap="xs" align="flex-end" wrap="wrap" pt={4} style={{ borderTop: '1px solid var(--mantine-color-dark-4)' }}>
	                                          <Select
	                                            size="xs"
	                                            placeholder="Zimmer zuweisen…"
	                                            data={assignableRoomOptions}
	                                            value={selectedRoom || null}
	                                            onChange={(value) =>
	                                              setGroupRoomSelections((prev) => ({ ...prev, [groupKey]: value ?? '' }))
	                                            }
	                                            style={{ flex: 1, minWidth: 140 }}
	                                            clearable
	                                          />
	                                          <Button
	                                            size="xs"
	                                            variant="light"
	                                            color={isCompleteGroup ? 'green' : isMutualGroup ? 'teal' : 'gray'}
	                                            disabled={!selectedRoom || assignGroupToRoomM.isPending}
	                                            loading={assignGroupToRoomM.isPending && assignGroupToRoomM.variables?.groupKey === groupKey}
	                                            onClick={() =>
	                                              assignGroupToRoomM.mutate({
	                                                participantIds: summary.participantIds,
	                                                roomId: selectedRoom,
	                                                groupKey,
	                                              })
	                                            }
	                                          >
	                                            {summary.names.length === 1 ? 'Ins Zimmer' : 'Alle ins Zimmer'}
	                                          </Button>
																												<ActionIcon
																													size="sm"
																													variant="subtle"
																													color="indigo"
																													title="Push-Meldung senden"
																													loading={sendRoomWishReminderM.isPending && pushingGroupKey === groupKey}
																													onClick={() => {
																														const userIds = summary.participantIds
																															.map((participantId) => participantBySubmissionId.get(participantId)?.userId)
																															.filter((id): id is string => Boolean(id));
																														if (userIds.length === 0) return;
																														sendRoomWishReminderM.mutate({ userIds, groupKey });
																													}}
																												>
																													<IconBell size={13} />
																												</ActionIcon>
	                                        </Group>
	                                      </Stack>
	                                    </Card>
	                                    );
	                                  })}
	                                </SimpleGrid>
	                                {roomWishGroupSummaries.length > 20 && (
	                                  <Text size="sm" c="dimmed">
	                                    {roomWishGroupSummaries.length - 20} weitere Gruppen im Export einsehbar.
	                                  </Text>
	                                )}
	                              </Stack>
	                            </Card>
	                          )}
                          {(roomPlanningParticipants.length > 0 || unmatchedRoomAssignments.length > 0) && (
                            <Card radius="sm" p="sm" className="contrast-surface-soft">
                              <Stack gap="xs">
                                <Group justify="space-between" wrap="wrap">
                                  <Text fw={600}>Teilnehmer</Text>
                                  <Group gap={6}>
                                    <Badge color="teal" variant="light" size="xs">
                                      {roomPlanningParticipants.length - missingRoomParticipants.length + unmatchedRoomAssignments.filter((a) => a.room_id).length} eingeplant
                                    </Badge>
                                    {missingRoomParticipants.length > 0 && (
                                      <Badge color="red" variant="light" size="xs">{missingRoomParticipants.length} fehlen noch</Badge>
                                    )}
                                    {appProfileRoomAssignments.length > 0 && (
                                      <Badge color="teal" variant="light" size="xs">{appProfileRoomAssignments.length} App-Profile</Badge>
                                    )}
                                    {extraRoomAssignments.length > 0 && (
                                      <Badge color="indigo" variant="light" size="xs">{extraRoomAssignments.length} zusätzlich</Badge>
                                    )}
                                  </Group>
                                </Group>
                                <Group gap="xs">
                                  {roomPlanningParticipants.map((participant) => {
                                    const isMissing = missingRoomParticipantIds.has(roomPlanningParticipantKey(participant));
                                    return (
                                      <Badge key={participant.submission.id} color={isMissing ? 'gray' : 'teal'} variant={isMissing ? 'outline' : 'light'}>
                                        {participant.displayName}
                                      </Badge>
                                    );
                                  })}
                                  {appProfileRoomAssignments.map((a) => (
                                    <Tooltip
                                      key={a.id}
                                      label="App-Profil verknüpft, aber keine passende Einreichung gefunden"
                                      withArrow
                                    >
                                      <Badge
                                        color={a.room_id ? 'teal' : 'gray'}
                                        variant={a.room_id ? 'light' : 'outline'}
                                      >
                                        {a.display_name}
                                      </Badge>
                                    </Tooltip>
                                  ))}
                                  {extraRoomAssignments.map((a) => (
                                    <Tooltip
                                      key={a.id}
                                      label={a.source === 'external' ? 'Extern (kein App-Konto)' : a.user_id ? 'App-Profil verknüpft' : 'Kein App-Konto zugeordnet'}
                                      withArrow
                                    >
                                      <Badge
                                        color={a.room_id ? (a.user_id ? 'teal' : a.source === 'external' ? 'indigo' : 'orange') : 'gray'}
                                        variant={a.room_id ? 'light' : 'outline'}
                                      >
                                        {a.display_name}
                                      </Badge>
                                    </Tooltip>
                                  ))}
                                </Group>
                              </Stack>
                            </Card>
                          )}
                          {(roomAssignmentsQ.data ?? []).length === 0 ? (
                            <Text size="sm" c="dimmed">
                              Noch keine Belegung gespeichert. Nutze „Vorschlag berechnen“.
                            </Text>
                          ) : (
                            <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="xs">
                              {[...bedroomRooms, null].map((room) => {
                                const key = room?.id ?? '';
                                const entries = assignmentsByRoom.get(key) ?? [];
                                if (room === null && entries.length === 0) return null;
                                const capacity = room?.capacity ?? 0;
                                const overbooked = room !== null && entries.length > capacity;
                                const isLocked = room?.is_locked ?? false;
                                const filled = entries.length;
                                return (
                                  <Card
                                    key={key || 'unassigned'}
                                    radius="sm"
                                    p="sm"
                                    className="contrast-surface-soft"
                                    style={isLocked
                                      ? { borderLeft: '3px solid var(--mantine-color-teal-5)', background: 'color-mix(in srgb, var(--mantine-color-teal-9) 15%, var(--mantine-color-dark-7))' }
                                      : overbooked
                                        ? { borderLeft: '3px solid var(--mantine-color-red-5)' }
                                        : dragOverRoomKey === (key || 'unassigned')
                                          ? { borderLeft: '3px solid var(--mantine-color-blue-5)', background: 'color-mix(in srgb, var(--mantine-color-blue-9) 20%, var(--mantine-color-dark-7))' }
                                          : undefined}
                                          onDragOver={(e) => {
                                            e.preventDefault();
                                            const targetKey = key || 'unassigned';
                                            setDragOverRoomKey((prev) => prev === targetKey ? prev : targetKey);
                                          }}
                                          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverRoomKey(null); }}
                                          onDrop={(e) => {
                                            e.preventDefault();
                                            setDragOverRoomKey(null);
                                            const aid = e.dataTransfer.getData('assignmentId');
                                            const fromRoom = e.dataTransfer.getData('fromRoomId');
                                            if (!aid || fromRoom === (key || 'unassigned')) return;
                                            updateAssignmentRoomM.mutate({ id: aid, roomId: room?.id ?? null });
                                          }}
                                  >
                                    <Stack gap={8}>
                                      {/* Header */}
                                      <Group justify="space-between" wrap="nowrap" align="flex-start">
                                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                          <Group gap={6} wrap="nowrap">
                                            {overbooked && <IconAlertTriangle size={14} color="var(--mantine-color-red-4)" />}
                                            {isLocked && !overbooked && <IconCircleCheck size={14} color="var(--mantine-color-teal-4)" />}
                                            <Text fw={700} size="sm" c={overbooked ? 'red' : undefined}>{room?.name ?? 'Nicht zugeordnet'}</Text>
                                          </Group>
                                          {room?.notes.trim() ? (
                                            <Text size="xs" c="dimmed" lineClamp={2}>
                                              {room.notes.trim()}
                                            </Text>
                                          ) : null}
                                        </Stack>
                                        <Group gap={4} style={{ flexShrink: 0 }}>
                                          {room && (
                                            <ActionIcon
                                              variant={roomSwapSource === room.id ? 'filled' : 'subtle'}
                                              color="violet"
                                              size="xs"
                                              title="Zimmer tauschen"
                                              onClick={() => setRoomSwapSource((prev) => prev === room.id ? null : room.id)}
                                            >
                                              <IconArrowsExchange size={12} />
                                            </ActionIcon>
                                          )}
                                          {room && (
                                            <ActionIcon
                                              variant={isLocked ? 'filled' : 'subtle'}
                                              color={isLocked ? 'teal' : 'gray'}
                                              size="xs"
                                              title={isLocked ? 'Zimmer entsperren' : 'Zimmer fixieren'}
                                              onClick={() => updateRoomM.mutate({ id: room.id, name: room.name, capacity: room.capacity, is_locked: !isLocked })}
                                            >
                                              {isLocked ? <IconLock size={12} /> : <IconLockOpen size={12} />}
                                            </ActionIcon>
                                          )}
                                        </Group>
                                      </Group>
                                      {/* Belegung label + progress bar */}
                                      {room && capacity > 0 && (
                                        <Box>
                                          <Group justify="space-between" mb={4}>
                                            <Text size="xs" c="dimmed">Belegung</Text>
                                            <Text size="xs" fw={600} c={overbooked ? 'red' : isLocked ? 'teal' : undefined}>
                                              {filled}/{capacity}
                                            </Text>
                                          </Group>
                                          <Progress
                                            value={Math.min(100, (filled / capacity) * 100)}
                                            size={4}
                                            radius="xl"
                                            color={overbooked ? 'red' : isLocked ? 'teal' : 'blue'}
                                          />
                                        </Box>
                                      )}
                                      {!room && <Badge color="yellow" variant="light" size="sm">{filled} ohne Zimmer</Badge>}
                                      {/* Room swap select */}
                                      {room && roomSwapSource === room.id && (
                                        <Group gap={4}>
                                          <Select
                                            size="xs"
                                            placeholder="Tauschen mit…"
                                            style={{ flex: 1 }}
                                            data={bedroomRooms
                                              .filter((r) => r.id !== room.id)
                                              .map((r) => {
                                                const cnt = (assignmentsByRoom.get(r.id) ?? []).length;
                                                return { value: r.id, label: `${r.name} (${cnt}/${r.capacity})` };
                                              })}
                                            onChange={(targetId) => {
                                              if (!targetId) return;
                                              swapRoomsM.mutate({ roomAId: room.id, roomBId: targetId });
                                            }}
                                          />
                                          <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setRoomSwapSource(null)}>
                                            <IconCheck size={12} />
                                          </ActionIcon>
                                        </Group>
                                      )}
                                      {/* Person list */}
                                      {entries.length === 0 ? (
                                        <Text size="xs" c="dimmed">Noch frei.</Text>
                                      ) : (
                                        <Stack gap={4}>
                                          {entries.map((assignment) => (
                                            <Stack key={assignment.id} gap={2}>
                                              {(() => {
                                                const isUnresolved = !assignment.user_id;
                                                const isResolving = resolvingAssignmentId === assignment.id;
                                                return (
                                                  <>
                                                    <Group
                                                      justify="space-between"
                                                      wrap="nowrap"
                                                      gap={4}
                                                      style={{
                                                        background: isUnresolved
                                                          ? 'color-mix(in srgb, var(--mantine-color-orange-9) 30%, var(--mantine-color-dark-6))'
                                                          : 'var(--mantine-color-dark-6)',
                                                        cursor: 'grab',
                                                        borderRadius: 'var(--mantine-radius-sm)',
                                                        padding: '2px 4px 2px 8px',
                                                        borderLeft: assignment.locked
                                                          ? '2px solid var(--mantine-color-teal-5)'
                                                          : isUnresolved
                                                            ? '2px solid var(--mantine-color-orange-5)'
                                                            : undefined,
                                                      }}
                                                      draggable
                                                      onDragStart={(e) => {
                                                        e.dataTransfer.setData('assignmentId', assignment.id);
                                                        e.dataTransfer.setData('fromRoomId', assignment.room_id ?? 'unassigned');
                                                        e.dataTransfer.effectAllowed = 'move';
                                                      }}
                                                      onDragEnd={() => setDragOverRoomKey(null)}
                                                    >
                                                      <Box style={{ minWidth: 0, flex: 1 }}>
                                                        <Text size="xs" fw={500} truncate c={assignment.locked ? 'teal' : isUnresolved ? 'orange.4' : undefined}>
                                                          {assignment.display_name}
                                                        </Text>
                                                        {assignment.source === 'external' && <Text size="xs" c="indigo.4">Extern</Text>}
                                                      </Box>
                                                      <Group gap={2} style={{ flexShrink: 0 }}>
                                                        {isUnresolved && (
                                                          <ActionIcon
                                                            variant={isResolving ? 'filled' : 'subtle'}
                                                            color="orange"
                                                            size="xs"
                                                            title="Person zuordnen oder als extern markieren"
                                                            onClick={() => setResolvingAssignmentId((prev) => prev === assignment.id ? null : assignment.id)}
                                                          >
                                                            <IconUserCheck size={11} />
                                                          </ActionIcon>
                                                        )}
                                                        <ActionIcon
                                                          variant={movePersonId === assignment.id ? 'filled' : 'subtle'}
                                                          color="blue"
                                                          size="xs"
                                                          title="In anderes Zimmer verschieben"
                                                          onClick={() => setMovePersonId((prev) => prev === assignment.id ? null : assignment.id)}
                                                        >
                                                          <IconArrowRight size={11} />
                                                        </ActionIcon>
                                                        <Switch
                                                          size="xs"
                                                          checked={assignment.locked}
                                                          title="Person fix"
                                                          onChange={(e) => toggleAssignmentLockedM.mutate({ id: assignment.id, locked: e.currentTarget.checked })}
                                                        />
                                                        <ActionIcon variant="subtle" color="red" size="xs" onClick={() => deleteAssignmentM.mutate(assignment.id)}>
                                                          <IconTrash size={11} />
                                                        </ActionIcon>
                                                      </Group>
                                                    </Group>
                                                    {isUnresolved && isResolving && (
                                                      <Stack
                                                        gap={4}
                                                        style={{
                                                          background: 'color-mix(in srgb, var(--mantine-color-orange-9) 20%, var(--mantine-color-dark-7))',
                                                          border: '1px solid var(--mantine-color-orange-8)',
                                                          borderRadius: 'var(--mantine-radius-sm)',
                                                          padding: '6px 8px',
                                                        }}
                                                      >
                                                        <Text size="xs" c="orange.3" fw={600}>
                                                          {assignment.display_name} — kein App-Konto verknüpft
                                                        </Text>
                                                        <Text size="xs" c="dimmed">
                                                          Profil auswählen damit die Zimmerbelegung in der App angezeigt wird, oder als Extern markieren.
                                                        </Text>
                                                        <Group gap={4}>
                                                          <Select
                                                            size="xs"
                                                            placeholder="App-Profil suchen…"
                                                            searchable
                                                            clearable
                                                            style={{ flex: 1, minWidth: 180 }}
                                                            data={profileSelectOptions}
                                                            disabled={roomProfileSuggestionsQ.isLoading || profileSelectOptions.length === 0}
                                                            onChange={(userId) => {
                                                              if (!userId) return;
                                                              resolveAssignmentUserM.mutate({ id: assignment.id, userId });
                                                              setResolvingAssignmentId(null);
                                                            }}
                                                          />
                                                          <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="indigo"
                                                            loading={resolveAssignmentExternalM.isPending}
                                                            onClick={() => {
                                                              resolveAssignmentExternalM.mutate(assignment.id);
                                                              setResolvingAssignmentId(null);
                                                            }}
                                                          >
                                                            Extern
                                                          </Button>
                                                          <ActionIcon size="xs" variant="subtle" color="gray" title="Schließen" onClick={() => setResolvingAssignmentId(null)}>
                                                            <IconCheck size={12} />
                                                          </ActionIcon>
                                                        </Group>
                                                      </Stack>
                                                    )}
                                                    {movePersonId === assignment.id && (
                                                      <Select
                                                        size="xs"
                                                        placeholder="Verschieben nach…"
                                                        data={bedroomRooms
                                                          .filter((r) => r.id !== assignment.room_id)
                                                          .map((r) => {
                                                            const cnt = (assignmentsByRoom.get(r.id) ?? []).length;
                                                            return { value: r.id, label: `${r.name} (${cnt}/${r.capacity})` };
                                                          })}
                                                        onChange={(value) => {
                                                          if (!value) return;
                                                          updateAssignmentRoomM.mutate({ id: assignment.id, roomId: value });
                                                          setMovePersonId(null);
                                                        }}
                                                      />
                                                    )}
                                                  </>
                                                );
                                              })()}
                                            </Stack>
                                          ))}
                                        </Stack>
                                      )}
                                    </Stack>
                                  </Card>
                                );
                              })}
                            </SimpleGrid>
                          )}
                        </Stack>
                      </Card>
                    </>
                  )}
                </Stack>
              </Card>
                </Tabs.Panel>
                )}
                <Tabs.Panel value="ablaufplan" pt="sm">
                  {selectedFormId && (
                    <AblaufplanTab formId={selectedFormId} formSlug={selectedForm?.slug ?? ''} />
                  )}
                </Tabs.Panel>
              </Tabs>
            </>
          )}
        </Stack>
      </Card>

      <Modal
        opened={jsonImportOpen}
        onClose={() => { setJsonImportOpen(false); setJsonImportText(''); }}
        title="Zimmer per JSON importieren"
        size="lg"
        centered
      >
        {(() => {
          let parsed: { label: string; capacity: number }[] | null = null;
          let parseError = '';
          try {
            const raw = JSON.parse(jsonImportText.trim());
            if (!Array.isArray(raw)) throw new Error('Erwartet ein JSON-Array.');
            parsed = raw.map((item, i) => {
              if (typeof item.label !== 'string' || !item.label.trim()) throw new Error(`Eintrag ${i + 1}: "label" fehlt oder ist leer.`);
              const cap = Number(item.capacity);
              if (!Number.isFinite(cap) || cap < 1) throw new Error(`Eintrag ${i + 1}: "capacity" muss eine Zahl ≥ 1 sein.`);
              return { label: item.label.trim(), capacity: Math.round(cap) };
            });
          } catch (e) {
            if (jsonImportText.trim()) parseError = e instanceof Error ? e.message : 'Ungültiges JSON.';
          }
          const existingNames = new Set((roomsQ.data ?? []).map((r) => r.name.trim().toLowerCase()));
          const newRooms = parsed?.filter((r) => !existingNames.has(r.label.toLowerCase())) ?? [];
          const skipped = (parsed?.length ?? 0) - newRooms.length;
          return (
            <Stack gap="md">
              <Textarea
                label="JSON einfügen"
                placeholder={'[\n  { "label": "1.01", "capacity": 4 },\n  { "label": "1.02", "capacity": 3 }\n]'}
                minRows={8}
                autosize
                value={jsonImportText}
                onChange={(e) => setJsonImportText(e.currentTarget.value)}
                error={parseError || undefined}
                styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              {parsed && newRooms.length > 0 && (
                <Stack gap={6}>
                  <Text size="sm" fw={600}>Vorschau — {newRooms.length} neue Zimmer{skipped > 0 ? `, ${skipped} bereits vorhanden (übersprungen)` : ''}:</Text>
                  <Group gap={6} wrap="wrap">
                    {newRooms.map((r) => (
                      <Badge key={r.label} variant="light" color="indigo" size="sm">
                        {r.label} · {r.capacity} Betten
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              )}
              {parsed && newRooms.length === 0 && (
                <Text size="sm" c="dimmed">Alle Zimmer im JSON sind bereits vorhanden.</Text>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => { setJsonImportOpen(false); setJsonImportText(''); }}>
                  Abbrechen
                </Button>
                <Button
                  color="indigo"
                  leftSection={<IconClipboard size={16} />}
                  disabled={!parsed || newRooms.length === 0}
                  loading={importRoomsFromJsonM.isPending}
                  onClick={() => parsed && importRoomsFromJsonM.mutate(newRooms)}
                >
                  {newRooms.length > 0 ? `${newRooms.length} Zimmer importieren` : 'Importieren'}
                </Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      <Modal
        opened={submissionDeleteTarget !== null}
        onClose={() => {
          if (!deleteSubmissionM.isPending) {
            setSubmissionDeleteTarget(null);
          }
        }}
        title="Einreichung löschen"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Diese Einreichung und alle zugehörigen Antworten werden endgültig gelöscht.
            Der Vorgang kann nicht rückgängig gemacht werden.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setSubmissionDeleteTarget(null)}
              disabled={deleteSubmissionM.isPending}
            >
              Abbrechen
            </Button>
            <Button
              color="red"
              loading={deleteSubmissionM.isPending}
              onClick={() => {
                if (!submissionDeleteTarget) return;
                deleteSubmissionM.mutate(submissionDeleteTarget.id);
              }}
            >
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
